/**
 * ForceBuilder.js
 * PSP/CRE Tool v3.0 - Force Structure Factory
 * 
 * VERSION 3.4.0 - F2-ARCH-010 (April 7, 2026):
 *   formatBuildResult() now always outputs array aliases alongside singular fields:
 *   BATTALION → battalions: [battalion]
 *   BRIGADE   → brigades:   [brigade]
 *   DIVISION  → divisions:  [division]
 *   CORPS already had divisions[], brigades[], battalions[] — unchanged.
 *   Singular fields (battalion, brigade, division) kept for Tab 1 UI backward compat.
 *   Consumers (casualtyCalculator, validationSystem, Tab 6, Tab 7) updated to read
 *   from arrays; singular fallbacks retained for pre-F2 persisted data.
 *   Fixes BUG-HQ-001: extractPersonnelBreakdown double-count guard added.
 *
 * VERSION 3.3.6 - BUG-199 (March 25, 2026):
 *   BUG-199 ROOT CAUSE: buildBrigade BUG-113 FINAL block excluded ARTILLERY from
 *   national standard strength normalization via `unitType !== 'ARTILLERY'`.
 *   buildDivision slot 3 defaults to ARTILLERY type. In multinational divisions where
 *   slot 3 carries a named national standard (e.g. GERMAN 4,800), the exclusion
 *   suppressed national standard application — brigade.strength remained at raw doctrinal
 *   sub_units sum (~2,600), causing ~275 casualty undercount for Bde 3 in all MND scenarios.
 *   BUG-199 was introduced by BUG-113 fix (v3.2.6, March 16, 2026).
 *   Fix: removed `&& unitType !== 'ARTILLERY'` from the BUG-113 FINAL condition.
 *   isCSS_BrigadeType() alone correctly excludes LOGISTICS/ENGINEER/SIGNAL/SUPPORT/MEDICAL.
 *   ARTILLERY is combat-support (not CSS) and must receive national standard strength
 *   when a named standard is present, exactly as MECHANIZED/HEAVY/INFANTRY do.
 *   NOTE: SUSPECT-001 (Bde 3 zero Role 1) also fixed here: defaultBrigadeTypes slot 3
 *   changed from 'ARTILLERY' to 'MECHANIZED'. Artillery brigades must be explicitly
 *   selected by the user — injecting ARTILLERY as a silent default caused Bde 3 to have
 *   no organic Role 1 facilities in every 3-brigade division build.
 *
 * VERSION 3.3.5 - BUG-184 + BUG-185 (March 24, 2026):
 *   BUG-184: UNIT_TYPE_TO_ASSET_PROFILE used key 'TRANSPORTATION' but BATTALION_TYPES
 *     uses 'TRANSPORT'. getAssetProfile('TRANSPORT') fell back to 'STANDARD' instead
 *     of 'LIGHT', over-allocating Role 1 assets for transport battalions.
 *     Fix: added TRANSPORT: 'LIGHT'. Legacy TRANSPORTATION key retained for persisted data.
 *   BUG-185: UNIT_TYPE_TO_ASSET_PROFILE missing: RECONNAISSANCE (stale CAVALRY key),
 *     AIRBORNE, AIR_ASSAULT, MOUNTAIN, AVIATION, MEDICAL_SUPPORT.
 *     All fell back to 'STANDARD' via || 'STANDARD' in getAssetProfile().
 *     STANDARD is correct for RECONNAISSANCE/AIRBORNE/AIR_ASSAULT/MOUNTAIN.
 *     AVIATION corrected to 'LIGHT' (smaller crew, co-located with flight surgeon,
 *     mirrors BRIGADE_TYPE_TO_ASSET_PROFILE.AVIATION = 'LIGHT_BRIGADE').
 *     MEDICAL_SUPPORT: null (no Role 1 — logistics only, needs_role1: false in
 *     unitTypes.js v2.12).
 *     Legacy CAVALRY key retained for any data persisted before unitTypes.js v2.11.
 *
 * VERSION 3.3.4 - BUG-181 (March 24, 2026): battalionNeedsRole1() used hardcoded
 *   exclusion list ['HQ', 'SIGNAL', 'MEDICAL']. All 15 BATTALION_TYPES entries now
 *   carry a needs_role1 field (unitTypes.js v2.11). Fix: read that field directly.
 *   Default true preserves safe behaviour for any unknown future type.
 *
 * VERSION 3.3.3 - BUG-176 FINAL (March 23, 2026): buildBattalion() strength uses explicit
 *   MANEUVER type list instead of category check. Root cause of category approach failing:
 *   category field ('CSS'/'SUPPORT'/'COMBAT') is correct but the real split is MANEUVER vs
 *   non-maneuver, which does not perfectly align with category. MANEUVER types (INFANTRY,
 *   MECHANIZED, ARMOR, RECONNAISSANCE, AIRBORNE, AIR_ASSAULT, MOUNTAIN, HEAVY, STRYKER,
 *   AVIATION) use national standard battalion strength. All other types (ARTILLERY, ENGINEERS,
 *   ADA, SIGNAL, LOGISTICS, MEDICAL, etc.) use BATTALION_TYPES[type].typical_strength.
 *   Architecture note: this fixes BATTALION echelon only. Brigade/division/corps remain
 *   top-down (national standard → BUG-113 scaling). True bottom-up deferred to v4 (ARCH-010).
 *
 * VERSION 3.3.2 - BUG-176: category-aware strength resolution (superseded by 3.3.3).
 *
 * VERSION 3.3.1 - BUG-171: national standard strength priority.
 *
 * VERSION 3.3.0 - BUG-131: Add generateScaledSubUnitsForTypeChange() — regenerates sub_units
 *   after a brigade type change in TacticalEditor so stale ARTILLERY/old-type sub_units no longer
 *   propagate wrong strength (2,600 vs 4,800) to calculator / Tab 5 / Tab 6. Called by Tab1
 *   handleSaveUnit when brigade unit_type differs from the stored value. Applies BUG-113
 *   national-standard proportional scaling. CSS/ARTILLERY/MEDICAL return null (no sub_units).
 *   CLEANUP: Removed dead empty `if (breakdown) {}` block in buildBrigade. (March 17, 2026)
 *
 * VERSION 3.2.6 - BUG-113 FINAL v2: Uses getDefaultStrength() + CSS/ARTILLERY exclusion — avoids undefined property access (March 16, 2026)
 *
 * VERSION 3.2.5 - BUG-113 FINAL: Sub_units scaled proportionally to national standard strength — validates correctly (March 16, 2026)
 *
 * VERSION 3.2.4 - BUG-113 COMPLETE FIX: National standard brigade strength applied AFTER sub_units recalculation — previous fix was overwritten by the sub_units strength sum (March 16, 2026)
 *
 * VERSION 3.2.3 - BUG-113: National standard brigade strength now applied for maneuver brigades — nationalStandards.js brigade.strength preferred over unitTypes.js composition.total_strength (March 16, 2026)
 *
 * VERSION 3.2.2 - BRIGADE_TYPE_TO_ASSET_PROFILE: added HEAVY, MOUNTAIN, MEDICAL (March 15, 2026):
 *   BUG FIX: HEAVY, MOUNTAIN, MEDICAL missing from BRIGADE_TYPE_TO_ASSET_PROFILE.
 *     getBrigadeAssetProfile() fell back to 'STANDARD' for these types — incorrect.
 *     HEAVY → 'HEAVY_BRIGADE' (same profile as ARMOR — both are heavy armoured).
 *     MOUNTAIN → 'LIGHT_BRIGADE' (lighter medical footprint; dismounted; pack transport).
 *     MEDICAL → 'LIGHT_BRIGADE' (medical brigades provide, not consume, R2 assets).
 *     Also adds HQ, AIR_DEFENSE, CAVALRY, AVIATION for completeness — all were missing
 *     and silently using 'STANDARD'. Now explicit:
 *     HQ → 'LIGHT_BRIGADE', AIR_DEFENSE → 'LIGHT_BRIGADE',
 *     CAVALRY → 'STANDARD', AVIATION → 'LIGHT_BRIGADE'.
 * VERSION 3.2.1 - ESLint clean-up after debug purge (March 5, 2026):
 * - REMOVED: `MTF_DEFAULT_OFFSETS` import — was only referenced in deleted debugLog calls.
 *   `calculateMTFPosition` retained (4 active call sites).
 * - REMOVED: `combatBnCount` / `supportBnCount` let declarations and ++ increments —
 *   both were only consumed by deleted debugLog calls. The role1 presence check
 *   (`if (battalionResult.role1)`) is preserved; only the dead counters are gone.
 *
 * VERSION 3.2.0 - DEBUG PURGE + CONSTANTS FIX (March 5, 2026):
 * - REMOVED: All 101 debug/console.log statements (DEBUG flag infrastructure, debugLog/debugWarn
 *   helpers, 🔴/🟡/🔵 diagnostic logs from Feb 13-14 sessions). These fired on every force
 *   build — hundreds of console calls per corps scenario. Significant performance improvement.
 * - REMOVED: MTF_CAPABILITIES_BRIGADE, MTF_CAPABILITIES_CORPS imports — only referenced in
 *   now-deleted debugLog calls. ESLint unused-import violations eliminated.
 * - FIX: medicalInventory bed fallbacks now use constants:
 *   role2_enhanced: `r.beds || ROLE_2_ENHANCED_DEFINITION.holding_capacity.beds` (was || 100)
 *   role3: `r.beds || ROLE_3_DEFINITION.holding_capacity.beds` (was || 200)
 *   APP_RULES 2.1 compliance — both constants already imported.
 *
 * VERSION 3.1.0 - FIX: medicalInventory hardcoded bed counts replaced with constants (February 25, 2026)
 * Build Date: February 25, 2026
 * 
 * v3.0.9 CHANGES - BREAKDOWN DIAGNOSTIC (February 14, 2026):
 * ============================================================================
 * ISSUE: breakdown field is undefined in localStorage despite code existing
 * - ForceService v3.0.6+ has breakdown calculation code (lines ~1648, ~2009, ~2237)
 * - nationalStandards.js v2.6 has breakdown_ratios for all standards
 * - But console shows NO breakdown debug logs
 * - localStorage shows breakdown: undefined for all units
 * 
 * DIAGNOSTIC: Added extensive console.log statements to trace breakdown calculation
 * - Brigade breakdown: Lines ~1650-1656 (7 new debug logs)
 * - Division breakdown: Lines ~2010-2015 (3 new debug logs)
 * - Corps breakdown: Lines ~2239-2244 (3 new debug logs)
 * 
 * These logs will show:
 * - What nationalStandard value is being used
 * - Whether NATIONAL_STANDARDS[nationalStandard] exists
 * - Whether breakdown_ratios field exists
 * - What the final breakdown object contains
 * 
 * v3.0.8 CHANGES - DIAGNOSTIC LOGGING (February 13, 2026):
 * ============================================================================
 * ISSUE: User still sees total_strength = 24,000 despite v3.0.7 fix
 * - ForceService v3.0.7 has correct calculation code (lines 2418-2428)
 * - Tab1 v4.6.2 has correct display code (no fallback)
 * - But localStorage shows total_strength = 24,000 (only brigades, missing HQs)
 * 
 * DIAGNOSTIC: Added critical console.log statements (always visible, not gated by DEBUG)
 * - Line ~2437: Shows breakdown of Corps HQ, Division HQs, Brigades, Total
 * - Line ~2458: Shows final total_strength value being returned
 * - These logs will help identify if calculation is wrong OR if value is being overwritten
 * 
 * EXPECTED OUTPUT when building 1 Corps + 2 Divisions + 6 Brigades:
 * ```
 * 🔵 [FORCESERVICE] Total Strength Calculated: {
 *   corps_hq: 2000,
 *   division_hqs: 2200,
 *   brigades: 24000,
 *   medical_brigades: 0,
 *   TOTAL: 28200
 * }
 * 🔵 [FORCESERVICE] Returning total_strength: 28200
 * ```
 * 
 * v3.0.7 CHANGES - CRITICAL BUG FIX: Total Force Strength Calculation (February 12, 2026):
 * ============================================================================
 * ISSUE: buildCorps() calculated totalStrength incorrectly - ONLY summed Division HQs
 * - User with 1 Corps (2k) + 2 Divisions (2.2k) + 6 Brigades (24.1k) saw "24,000 total"
 * - Actual total: 28,300 personnel (15% UNDERCOUNT!)
 * - Impact: ALL downstream calculations wrong (casualties, medical, supply)
 * 
 * ROOT CAUSE (Lines 2379-2380):
 * ```javascript
 * // OLD (WRONG):
 * let totalStrength = 0;
 * divisions.forEach(div => totalStrength += div.strength || 0);
 * // Only summed Division HQs (1,000 + 1,200 = 2,200), missed Corps and Brigades!
 * ```
 * 
 * FIX (Lines 2374-2400):
 * ```javascript
 * // NEW (CORRECT):
 * let totalStrength = corps.strength || 0;              // Corps HQ: 2,000
 * divisions.forEach(div => totalStrength += div.strength || 0);  // Div HQs: 2,200
 * brigades.forEach(bde => totalStrength += bde.strength || 0);   // Brigades: 24,100
 * medicalBrigades.forEach(mb => totalStrength += mb.brigade?.strength || 0);
 * // TOTAL: 28,300 ✓
 * ```
 * 
 * ADDED: Detailed breakdown logging for verification
 * - Shows: Corps HQ, Division HQs, Brigades, Medical Brigades, TOTAL
 * - Example output: "Corps HQ: 2000, Division HQs: 2200, Brigades: 24100, TOTAL: 28300"
 * 
 * RESULT:
 * - Tab1 summary now shows correct total (28,300 not 24,000)
 * - Calculator receives correct force size
 * - All casualty calculations accurate
 * - Medical requirements correct
 * - Supply calculations accurate
 * 
 * v3.0.6 CHANGES - BUG-065 FIX: Personnel Breakdown Calculation (February 11, 2026):
 * ============================================================================
 * ISSUE: Calculator used flat 4%/37% percentages causing incorrect HQ/Support casualties
 * - "HQ" means different things: Corps HQ = 90% support, Brigade HQ = 100% command
 * - Tab5 showed 960 HQ (should be 1,700), 8,880 support (should be 8,250)
 * FIX: Read breakdown_ratios from nationalStandards.js v2.6, calculate actual numbers
 * 
 * AFFECTED FUNCTIONS (4 additions):
 * 1. buildBattalion() - Line ~1009
 *    - READS: standard.battalion.breakdown_ratios { command: 0.50, support: 0.50 }
 *    - CALCULATES: 100 HQ × 0.50 = 50 command, 50 support
 *    - PASSES: breakdown object to Battalion constructor
 * 
 * 2. buildBrigade() - Line ~1529
 *    - READS: standard.brigade.breakdown_ratios { command: 1.00, support: 0.00 }
 *    - CALCULATES: 200 HQ × 1.00 = 200 command, 0 support
 *    - PASSES: breakdown object to Brigade constructor
 *    - NOTE: Brigade support is separate battalion (not in HQ)
 * 
 * 3. buildDivision() - Line ~1853
 *    - READS: standard.division.breakdown_ratios { command: 0.15, support: 0.85 }
 *    - CALCULATES: 800 HQ × 0.15 = 120 command, 680 support
 *    - PASSES: breakdown object to Division constructor
 * 
 * 4. buildCorps() - Line ~2090
 *    - READS: standard.corps.breakdown_ratios { command: 0.10, support: 0.90 }
 *    - CALCULATES: 1500 HQ × 0.10 = 150 command, 1350 support
 *    - PASSES: breakdown object to Corps constructor
 * 
 * DATA FLOW:
 * nationalStandards.js v2.6 (breakdown_ratios) 
 *   → ForceService v3.0.6 (calculates breakdown)
 *     → Tab1 (stores forceBuilderResult.corps.breakdown)
 *       → Tab2 (preserves via spread)
 *         → Calculator v5.2.0 (extracts breakdown, applies different rates)
 *           → Tab5 (displays correct numbers)
 * 
 * RESULT:
 * - Command: 0.4% daily casualty rate (protected, rear)
 * - Support: 0.7% daily casualty rate (vulnerable convoys)
 * - Combat: 1.5% daily casualty rate (direct engagement)
 * - Tab5 now shows: 1,700 HQ (correct), 8,250 support (correct)
 * 
 * VERSION 3.0.5 - BUG FIX: Brigade HQ position uses type offset at build time (February 11, 2026)
 * - BUG: buildDivision used geometric formula (division.position × 0.3 + i × 5) for brigade HQ
 *   This formula ignored unit type entirely — getBrigadeHQPosition() calls in buildBrigade
 *   were dead code because config.position_feba_km was always pre-set by the formula
 * - FIX: Replace geometric formula with getBrigadeHQPosition(brigadeStandard, brigadeType)
 *   Result: US Artillery = 25km (20+5), US Infantry = 20km (20+0), NATO Medical = 33km (18+15)
 * - Consistent with Tab1_ForceComposition.jsx v4.3.1 fix (handleUnitTypeChange, same root cause)
 *
 * VERSION 3.0.4 - National Standard Validation (February 5, 2026)
 * Build Date: February 5, 2026
 * 
 * v3.0.4 CRITICAL FIX - REMOVED ALL NATIONALSTANDARD FALLBACKS (February 5, 2026):
 * ============================================================================
 * ISSUE: All builder functions had || 'NATO' fallbacks allowing missing national standards
 * FIX: Removed fallbacks from 5 functions, added proper error throwing
 * 
 * AFFECTED FUNCTIONS (5 fixes):
 * 1. buildBattalion() - Line ~946
 *    - BEFORE: const nationalStandard = config.nationalStandard || 'NATO';
 *    - AFTER: const nationalStandard = config.nationalStandard;
 *    - ERROR: "buildBattalion: Missing nationalStandard for battalion 'X'"
 * 
 * 2. buildMedicalBrigade() - Line ~1090
 *    - BEFORE: const nationalStandard = config.nationalStandard || 'NATO';
 *    - AFTER: const nationalStandard = config.nationalStandard;
 *    - ERROR: "buildMedicalBrigade: Missing nationalStandard for medical brigade 'X'"
 * 
 * 3. buildBrigade() - Line ~1373
 *    - BEFORE: const nationalStandard = config.nationalStandard || 'NATO';
 *    - AFTER: const nationalStandard = config.nationalStandard;
 *    - ERROR: "buildBrigade: Missing nationalStandard for brigade 'X'"
 * 
 * 4. buildDivision() - Line ~1765
 *    - BEFORE: const nationalStandard = config.nationalStandard || 'NATO';
 *    - AFTER: const nationalStandard = config.nationalStandard;
 *    - ERROR: "buildDivision: Missing nationalStandard for division 'X'"
 * 
 * 5. buildCorps() - Line ~1997
 *    - BEFORE: const nationalStandard = config.nationalStandard || 'NATO';
 *    - AFTER: const nationalStandard = config.nationalStandard;
 *    - ERROR: "buildCorps: Missing nationalStandard for corps 'X'"
 * 
 * INTEGRATION WITH TAB1 v4.2:
 * - Tab1 now disables "Quick Build" button until all flags selected (proactive)
 * - ForceService validates at build time (defensive)
 * - Two-layer validation ensures no builds with missing national standards
 * - Future-proof: Works for German, Polish, French, any future additions
 * 
 * WHY THIS MATTERS:
 * - National standard determines unit strength (ROU: 4000, NATO: 5000, US: 4500)
 * - National standard determines HQ positioning (ROU: 15-35km, US: 20-40km)
 * - National standard determines available unit types
 * - Fallback to 'NATO' hides validation errors and creates inconsistent data
 * 
 * VERSION 3.0.3 - PHASE 2.1 COMPLETE: HQ Override Integration (February 4, 2026)
 * Build Date: February 4, 2026
 * 
 * v3.0.3 PHASE 2.1 STEP 7 - HQ OVERRIDE INTEGRATION (February 4, 2026):
 * ============================================================================
 * FINAL STEP: Added manual HQ position override capability from Tab2 Expert Mode
 * 
 * NEW FUNCTIONALITY:
 * - ForceService now checks for manual HQ overrides from Tab2 before using calculations
 * - Override path: Tab2 → operation_parameters.hq_override_config → ForceService
 * - When enabled, user-specified positions take precedence over doctrine
 * 
 * AFFECTED FUNCTIONS (4 override checks added):
 * 1. buildBattalion() - Line ~905
 *    - Checks: scenario?.operation_parameters?.hq_override_config?.battalion_hq_km
 *    - Precedence: Manual Override → unitTypeConfig → National Standard → 8km fallback
 * 
 * 2. buildBrigade() - Lines ~1327-1370
 *    - Checks: scenario?.operation_parameters?.hq_override_config?.brigade_hq_km
 *    - Precedence: Manual Override → getBrigadeHQPosition() → fallback
 *    - Applied to: CSS brigades, Artillery brigades, Combat brigades
 * 
 * 3. buildDivision() - Line ~1738
 *    - Checks: scenario?.operation_parameters?.hq_override_config?.division_hq_km
 *    - Precedence: Manual Override → National Standard → 25km fallback
 * 
 * 4. buildCorps() - Line ~1987
 *    - Checks: scenario?.operation_parameters?.hq_override_config?.corps_hq_km
 *    - Precedence: Manual Override → National Standard → 50km fallback
 * 
 * OVERRIDE PRECEDENCE (Highest to Lowest):
 * 1. Manual Override (Tab2 Expert Mode) - User-specified position
 * 2. Calculated Position (Phase 2.1 Step 2) - National base + Brigade type offset
 * 3. Defensive Fallback - Constants from nationalStandards.js
 * 
 * USE CASE EXAMPLES:
 * - Terrain Adjustment: User sets brigade at 30km instead of 20km (mountainous area)
 * - Threat Response: User sets corps at 80km instead of 60km (artillery threat)
 * - Training Exercise: Instructor tests different positioning doctrines
 * - Coalition Ops: User manually aligns positions with allied forces
 * 
 * DEBUG LOGGING:
 * - Override active: "🎯 Using HQ Override: Brigade HQ at 30km (manual override from Tab2)"
 * - Doctrine used: "🎯 Brigade HQ Position: 20km (US base + MECHANIZED offset)"
 * 
 * INTEGRATION WITH PHASE 2.1:
 * - Step 1: brigadeTypeOffsets.js created ✓
 * - Step 2: ForceService dynamic calculation ✓
 * - Step 3: Calculator verification ✓
 * - Step 4: Tab1 cleanup ✓
 * - Step 5: Tab3 verification ✓
 * - Step 6: Tab2 section move ✓
 * - Step 7: ForceService override integration ✓ (THIS VERSION)
 * 
 * PHASE 2.1: 100% COMPLETE! All 7 steps implemented.
 * ============================================================================
 * 
 * v3.0.2 PHASE 2.1 - HQ POSITIONING SYSTEM INTEGRATION (February 3, 2026):
 * ============================================================================
 * CRITICAL INTEGRATION: Replaced hardcoded HQ positions with dynamic calculation
 * 
 * WHAT CHANGED:
 * - Integrated brigadeTypeOffsets.js (new constant) with nationalStandards.js v2.4
 * - Architecture: Brigade HQ Position = National Base + Unit Type Offset
 * - Removed all hardcoded HQ position values (15km, 20km, 35km, 50km, 100km)
 * - Now uses getBrigadeHQPosition(nationalStandard, brigadeType) function
 * 
 * AFFECTED FUNCTIONS:
 * 1. buildBrigade() - Lines 1261, 1269, 1274
 *    - OLD: brigadePositionFeba = 15 (combat), 20 (artillery), 35 (CSS)
 *    - NEW: brigadePositionFeba = getBrigadeHQPosition(nationalStandard, unitType)
 *    - Result: Romanian Artillery = 15km base + 5km offset = 20km
 *              US CSS = 20km base + 20km offset = 40km
 * 
 * 2. buildCorps() - Lines 1870-1871
 *    - OLD: position_feba_km = 100, hq_position_km = 80 (hardcoded)
 *    - NEW: Uses standard.corps.hq_position_feba_km from nationalStandards.js
 *    - Result: Romanian 50km, NATO 55km, US 60km (not 100km for all)
 * 
 * 3. buildDivision() - Line 1633
 *    - OLD: position_feba_km = 50 (hardcoded)
 *    - NEW: Uses standard.division.hq_position_feba_km from nationalStandards.js
 *    - Result: Romanian 25km, NATO 28km, US 30km (not 50km for all)
 * 
 * 4. buildBattalion() - Line 835
 *    - OLD: Fallback = 8 (hardcoded)
 *    - NEW: Fallback = standard.battalion.hq_position_feba_km
 *    - Result: Romanian 8km, NATO 10km, US 12km (national differentiation)
 * 
 * NATIONAL DIFFERENTIATION NOW WORKS:
 * Before: All nations used same hardcoded values
 * After:  Each nation uses doctrine-based positions
 * 
 * Example - Artillery Brigade:
 * - Romanian: 15km (forward base) + 5km (artillery offset) = 20km
 * - NATO:     18km (balanced base) + 5km (artillery offset) = 23km
 * - US:       20km (conservative base) + 5km (artillery offset) = 25km
 * 
 * Example - CSS Brigade:
 * - Romanian: 15km base + 20km offset = 35km
 * - NATO:     18km base + 20km offset = 38km
 * - US:       20km base + 20km offset = 40km
 * 
 * DOCTRINAL SOURCES:
 * - nationalStandards.js v2.4: National base positions per echelon
 * - brigadeTypeOffsets.js v1.0: Universal tactical offsets by brigade type
 * - FM 3-96, ATP 4-90, FM 4-02, AJMedP-1, Ukraine 2022-2024 analysis
 * 
 * CALCULATOR IMPACT:
 * - Calculator already expects brigade.position_feba_km field ✓
 * - No calculator changes needed - field name unchanged ✓
 * - Values now vary by nation instead of being hardcoded ✓
 * 
 * INTEGRATION:
 * - Requires nationalStandards.js v2.4 (Phase 1)
 * - Requires brigadeTypeOffsets.js v1.0 (Step 1)
 * - Tab2 Expert Mode will use this for HQ override feature (future)
 * 
 * ============================================================================
 * 
 * VERSION 3.0.1 - PHASE 2.1: Strict unit_type Validation (February 3, 2026)
 * Build Date: February 3, 2026
 * 
 * v3.0.1 PHASE 2.1 - UNIT_TYPE VALIDATION FIX (February 3, 2026):
 * ============================================================================
 * BUG FIX: Removed silent fallback that violated v3.0 "NO FALLBACKS" principle
 * 
 * WHAT WAS BROKEN:
 * - buildBrigade() line 1196 had: `const unitType = config.unit_type || 'MECHANIZED'`
 * - This fallback allowed brigades to be created without unit_type
 * - Result: Missing unit_type → defaults to MECHANIZED → wrong sub-units generated
 * - User could save brigade without selecting Unit Type (silent data corruption)
 * 
 * WHAT'S FIXED:
 * - Removed the || 'MECHANIZED' fallback entirely
 * - Added explicit validation at start of buildBrigade()
 * - Throws user-friendly error if unit_type missing:
 *   "[v3.0 VALIDATION] buildBrigade requires 'unit_type' parameter.
 *    Brigade: "1st Brigade"
 *    Please select a Unit Type (MECHANIZED, HEAVY, INFANTRY, etc.) before saving.
 *    This is required for doctrinal sub-unit generation in v3.0."
 * 
 * ERROR HANDLING:
 * - Early validation before any processing
 * - Clear error message indicates what's missing
 * - Explains WHY it's required (doctrinal sub-units)
 * - Guides user to fix the issue
 * 
 * INTEGRATION:
 * - Requires Tab1_ForceComposition.jsx v4.0 (frontend validation)
 * - Tab1 now marks Unit Type as required and disables Save button
 * - User sees error in UI before ForceService is called
 * - This is a safety net for defensive programming
 * 
 * ============================================================================
 * 
 * VERSION 3.0.0 - PHASE 2: Doctrinal Sub-Unit Generation (NO FALLBACKS)
 * Build Date: February 3, 2026
 * 
 * v3.0.0 PHASE 2 - DOCTRINAL COMPOSITION AUTO-GENERATION (February 3, 2026):
 * ============================================================================
 * CRITICAL ARCHITECTURE CHANGE: NO FALLBACKS - Strict Data Validation
 * 
 * WHAT'S NEW:
 * - Brigades now auto-generate sub_units[] from unitTypes.js doctrinal_composition
 * - Each brigade generates 5-8 sub-units (battalions/squadrons/companies) automatically
 * - Sub-units used for accurate casualty calculations (each with own modifier)
 * - Strict validation: THROWS ERRORS if data incomplete (no || defaults)
 * 
 * NEW FUNCTIONS:
 * 1. generateSubUnitsFromDoctrine(unitType, parentName, parentId, echelon)
 *    - Reads doctrinal_composition from unitTypes.js
 *    - Generates complete sub_units array with all required fields
 *    - Supports name patterns: {parent}, {index}, {letter}
 *    - THROWS ERRORS if composition missing or invalid
 * 
 * 2. validateBrigadeData(brigade)
 *    - Validates complete brigade before return
 *    - REQUIRES: name, unit_type, strength, sub_units[]
 *    - Validates each sub-unit has unit_type and strength
 *    - Verifies total strength matches sum of sub-units
 *    - THROWS ERRORS if validation fails
 * 
 * 3. validateBattalionData(battalion)
 *    - Validates complete battalion before return
 *    - REQUIRES: name, unit_type, strength
 *    - THROWS ERRORS if validation fails
 * 
 * MODIFIED FUNCTIONS:
 * - buildBrigade(): Now generates sub_units from doctrine, validates before return
 * - buildBattalion(): Now validates before return (both paths: with/without Role 1)
 * 
 * DATA STRUCTURE CHANGE:
 * Brigade now includes:
 * {
 *   id, name, unit_type, strength,
 *   composition_source: 'doctrinal',  // NEW: Marks auto-generated
 *   sub_units: [                      // NEW: Array of sub-unit objects
 *     {
 *       id, type, unit_type, strength, name, parent_id, notes,
 *       composition_source: 'doctrinal'
 *     }
 *   ]
 * }
 * 
 * ACCURACY IMPROVEMENT:
 * Before: Brigade × single modifier (e.g., MECHANIZED × 1.0)
 * After:  Sum of (each sub-unit × its modifier)
 * Result: 15-21% more accurate casualty calculations for combat brigades
 * 
 * DOCTRINAL SOURCES:
 * - ATP-3.2.1: NATO Land Tactics
 * - FM 3-96: Brigade Combat Team (2016-2023)
 * - FM 3-21.10: Infantry Rifle Company
 * - FM 3-09: Field Artillery Operations
 * - ATP 4-90: Brigade Support Battalion
 * - CBO Interactive Force Structure Tool (2023)
 * 
 * BACKWARDS COMPATIBILITY: NONE
 * - This is Phase 2 of v3.0 migration
 * - Old data without sub_units will cause calculator to throw errors
 * - This is INTENTIONAL - forces correct data generation
 * 
 * NEXT PHASE (Phase 3):
 * - Update Calculator to use sub_units for casualty calculations
 * - Remove all calculator fallbacks (casualty_modifier || 1.0, etc.)
 * - Add calculator validation at entry point
 * 
 * ============================================================================
 * 
 * VERSION 2.13.2 - CRITICAL BUG FIX: Use hq_personnel for Corps/Division
 * Build Date: February 1, 2026
 * 
 * v2.13.2 CRITICAL BUG FIX (February 1, 2026):
 * - BUG FIX: Corps/Division strength now uses hq_personnel from nationalStandards.js v2.3
 * - BEFORE: Used getDefaultStrength() which returned total organizational size
 *   - Romanian Corps: defaultStrength = 36000 (total force, WRONG!)
 *   - Romanian Division: defaultStrength = 12000 (total force, WRONG!)
 * - AFTER: Uses NATIONAL_STANDARDS[nation].corps.hq_personnel
 *   - Romanian Corps: hq_personnel = 1500 (HQ only, CORRECT!)
 *   - Romanian Division: hq_personnel = 800 (HQ only, CORRECT!)
 * - IMPACT: Corps/Division DNBI calculations now use correct HQ personnel
 * - RESULT: Romanian Corps DNBI: 540 → 23 cases/day (96% reduction)
 * - RESULT: Romanian Division DNBI: 180 → 12 cases/day (93% reduction)
 * - Added NATIONAL_STANDARDS import from nationalStandards.js
 * - Updated buildDivision() line 1232: Uses standard.division.hq_personnel
 * - Updated buildCorps() line 1448: Uses standard.corps.hq_personnel
 * - Removed dependency on getDefaultStrength() for Division/Corps
 * 
 * v2.13.0 CRITICAL BUG FIXES:
 * - BUG FIX: Corps HQ strength was overwritten with division totals (Line 1606)
 *   - ISSUE: corps.strength (Corps HQ ~2000) was set to sum of divisions (~20000-46000)
 *   - FIXED: Removed overwrite, keep corps.strength as Corps HQ personnel only
 *   - ADDED: total_strength field to return object (stores force total separately)
 *   - Result: Corps DNBI calculations now use correct 2000 instead of 46000
 * 
 * - BUG FIX: Division HQ strength was overwritten with brigade totals (Line 1387)
 *   - ISSUE: division.strength (Division HQ ~1000) was set to sum of brigades (~10000-15000)
 *   - FIXED: Removed overwrite, keep division.strength as Division HQ personnel only
 *   - ADDED: total_strength field to return object (stores force total separately)
 *   - Result: Division DNBI calculations now use correct 1000 instead of 10000
 * 
 * - CONSOLE LOGGING: Added debug logs showing:
 *   "Total force strength: 20800 (Corps HQ: 2000)"
 *   "Total force strength: 10400 (Division HQ: 1000)"
 * 
 * - IMPACT: Fixes support unit calculations (now receives proper total_strength)
 * - IMPACT: Fixes echelon DNBI calculations (Corps/Division use correct HQ strength)
 * 
 * v2.12.2 DOCUMENTATION CLARIFICATION:
 * - Added clear distinction between SUPPORT (US) and LOGISTICS (NATO) brigades
 * - SUPPORT = US multifunctional sustainment (supply + transport + maintenance + logistics)
 * - LOGISTICS = NATO supply-focused (supply + transport only)
 * - Added display label recommendations for Tab1 UI
 * - NOTE: Tab1 should display these as different options, not both as "Sustainment Brigade"
 * 
 * v2.12.1 ESLint FIX:
 * - Fixed 'isCSS_BrigadeType' is assigned but never used (no-unused-vars)
 * - Now uses isCSS_BrigadeType() helper in 3 locations:
 *   - Line 859: CSS brigade type check
 *   - Line 883: isSpecialType determination
 *   - Line 945: CSS battalion positioning
 * 
 * v2.12.0 UTIL-003 CONSOLE CLEANUP:
 * - Removed excessive console.log statements (46 total)
 * - Added DEBUG flag for conditional logging (default: false)
 * - Production-ready: No console spam
 * - Development: Set DEBUG = true to enable logging
 * 
 * v2.11.0 UT-001 CSS BRIGADE TEMPLATES:
 * - Added dedicated templates for Combat Service Support brigades:
 *   - SUPPORT: Supply, Transportation, Maintenance battalions (2200 personnel)
 *   - LOGISTICS: Supply, Transportation battalions (1800 personnel)
 *   - ENGINEER: Combat Engineer, Construction battalions (2800 personnel)
 *   - SIGNAL: Signal, Network Operations battalions (1600 personnel)
 * - CSS brigades position further from FEBA (25-40km)
 * - CSS battalions get Role 1 (except HQ, SIGNAL, MEDICAL)
 * - Proper mission assignments (SUSTAINMENT, MOBILITY_SUPPORT, etc.)
 * 
 * v2.10.0 FB-003 LOGISTICS Battalions Get Role 1:
 * - Removed LOGISTICS from noRole1Types exclusion list
 * - LOGISTICS battalions (500+ personnel) now correctly get Role 1 facilities
 * - Transport convoys, depot workers can be injured and need medical care
 * - Only HQ, SIGNAL, and MEDICAL battalions remain excluded:
 *   - HQ: Small staff (~50), co-located with units that have Role 1
 *   - SIGNAL: Small staff (~100), co-located with other units
 *   - MEDICAL: Personnel treated in facilities they operate
 * 
 * v2.9.0 MTF-002 IMPORT CAPABILITIES FROM CONSTANTS:
 * - All MTF bed counts, surgical teams, ICU beds now imported from mtfCapabilities.js
 * - Role 1: 6 beds (was hardcoded 10)
 * - Role 2 Basic: 30 beds, 2 surgical teams (was hardcoded 50)
 * - Role 2 Enhanced: 40 beds, 4 surgical teams, 10 ICU (was hardcoded 100/6/20)
 * - Role 3: 150 beds, 8 surgical teams, 40 ICU (was hardcoded 200/10/40)
 * - Removed MEDICAL_BRIGADE_CONFIG hardcoded values
 * - Personnel counts now from mtfCapabilities.js
 * 
 * v2.8.0 MTF-001 POSITION CALCULATION FROM PARENT HQ:
 * - MTF positions now calculated using doctrine: Position = Parent HQ + Role Offset
 * - Imports calculateMTFPosition() from mtfDefaultOffsets.js
 * - Role 1: Battalion HQ position + 3km offset
 * - Role 2 Basic: Brigade HQ position + 6km offset
 * - Role 2 Enhanced: Division HQ position + 10km offset
 * - Role 3: Corps HQ position + 20km offset
 * - Removed all hardcoded position_feba_km values
 * - Positions now properly cascade from Corps → Division → Brigade → Battalion
 * 
 * v2.7.0 EA-001/EA-002 ORGANIC ASSET ALLOCATION:
 * - All facilities now have organic_assets based on unit type and national standard
 * - Role 1: Assets from ROLE1_ORGANIC_ASSETS by battalion profile (STANDARD/HEAVY/ARTILLERY/LIGHT)
 * - Role 2B: Assets from ROLE2_BASIC_ORGANIC_ASSETS by brigade profile
 * - Role 2E: Assets from ROLE2_ENHANCED_ORGANIC_ASSETS by national standard
 * - Role 3: Assets from ROLE3_ORGANIC_ASSETS
 * - Added attached_assets and detached_assets arrays for asset transfer tracking
 * - Added effective_assets calculation (organic + attached - detached)
 * - Integrated with evacuationAssetAllocation.js constants
 * 
 * v2.6.1 UT-001 ENGINEER LABEL FIX:
 * - Fixed ENGINEER→ENGINEERS key mismatch in typeLabels
 * - Added both ENGINEERS and ENGINEER keys for backward compatibility
 * - Updated UNIT_TYPE_TO_ASSET_PROFILE with ENGINEERS key
 * - Ensures engineer battalions display as "Engineer" not "ENGINEERS"
 * 
 * v2.6.0 ORGANIC DIVISION ROLE 2E:
 * - FB-003: Each Division now automatically gets an organic Role 2E (Field Hospital)
 * - NATO doctrine: Division has organic medical support at Role 2 Enhanced level
 * - Role 2E created with 100 beds, 4 surgical teams, 20 ICU beds
 * - Positioned at 40km from FEBA (Division rear area)
 * - Evacuation chain: Role 2B → Role 2E properly configured
 * - Separate from Medical Brigade R2E (which provides ADDITIONAL capacity)
 * 
 * v2.5.0 PHASE 2 EVACUATION CHAIN INTEGRATION:
 * - EA-001: Role 1 now receives unit_type for organic asset allocation
 * - EA-002: Role 2 receives brigade_type and supported_role1s list
 * - EA-003: Full evacuation chain configured (Role 1→2B→2E→3)
 * - Added configureEvacuationChain() method
 * - All facilities now have proper organic/attached/detached assets
 * 
 * v2.4.0 PHASE 1 FIXES:
 * - FB-001: Medical Brigade now CREATES actual Role 2E and Role 3 facility objects
 * - FB-002: ALL brigades now get Role 1 facilities for their battalions
 * - UT-001: Added proper ARTILLERY brigade handling with artillery battalion template
 * 
 * v2.3.3 BUGFIX:
 * - FIXED: "undefined/Brigade Name" in battalion names
 * 
 * v2.3.2 BUGFIX:
 * - FIXED: buildCorps now returns `brigades` array
 * - FIXED: buildCorps now returns `battalions` array
 * - FIXED: buildDivision now returns `battalions` array
 * 
 * Author: Dr. Tatar Radu MD
 * Institution: Romanian Military Medical Institute
 * Date: January 18, 2026
 * Version: 2.13.0
 *
 * v2.15.0 CHANGES (March 13, 2026) — ENH-018 Phase F: Lead/Framework Nation:
 *   - buildCorps(): reads config.corpsLeadNation (optional, only meaningful when
 *     nationalStandard === 'MULTINATIONAL').
 *   - When corpsLeadNation is set, calls getSupportUnitsForEchelon(corpsLeadNation, 'corps')
 *     instead of getSupportUnitsForEchelon('MULTINATIONAL', 'corps').
 *   - When null/absent: existing behaviour unchanged (averaged MULTINATIONAL data).
 *   - Division support troops unchanged — each division uses its own nationalStandard.
 *   - All other files unchanged (calculator reads support_troops as before).
 *   - Backward-compatible: pre-v4.11 scenarios without corpsLeadNation use MULTINATIONAL.
 *
 * v2.14.0 CHANGES (March 11, 2026) — Phase A: Per-Division Named Unit Arrays:
 *   - buildCorps(): added divisionSupportGroups[] array, populated inside division loop.
 *     Each entry: { division_id, division_name, national_standard, organic_units[],
 *     total_organic_strength, by_category }. Sourced from divisionResult.support_troops
 *     (the full getSupportUnitsForEchelon() return) per division.
 *   - buildCorps() return: added division_support_groups field.
 *   - Existing divisions[] array unchanged — downstream consumers unaffected.
 *   - Backwards-compatible: returns [] if no divisions built.
 *   - Tab1 ForceStructurePreview will read:
 *       forceBuilderResult.support_troops.organic_units[]  (corps group)
 *       forceBuilderResult.division_support_groups[i].organic_units[]  (per division)
 *
 * v2.13.0 CHANGES (March 11, 2026) — Corps/Division Support Troops Wiring:
 * - Added getSupportUnitsForEchelon import from nationalStandards.js
 * - buildDivision(): calls getSupportUnitsForEchelon(nationalStandard, 'division'),
 *   adds total_organic_strength to totalStrength, exposes support_troops in return
 * - buildCorps(): calls getSupportUnitsForEchelon(nationalStandard, 'corps'),
 *   accumulates divisionSupportStrength from each buildDivision result,
 *   adds both to totalStrength, exposes support_troops + division_support_strength in return
 * - Constants work was complete (nationalStandards.js v2.8 with 7 nations);
 *   this change wires the existing data into force object totals.
 *   Calculator and Tab5 will consume support_troops in a follow-on step.
 */

import { Battalion, Brigade, Division, Corps } from '../models/force';
import { 
  Role1Facility, 
  Role2BasicFacility, 
  Role2EnhancedFacility,
  Role3Facility
} from '../models/medical';
import { NATIONAL_STANDARDS, getDefaultStrength, getSubordinateCount,
  getSupportUnitsForEchelon  // v2.13.0: Corps/Division support troops wiring
} from '../constants/nationalStandards';
// v2.4 Phase 2.1: Brigade HQ positioning with base + offset architecture
import { getBrigadeHQPosition } from '../constants/brigadeTypeOffsets';
import { BRIGADE_CARDS_ON, brigadeCardsFor, brigadeCardsTotal } from '../constants/brigadeCards';
import { normalizeUnitTypeKey, lookupByUnitType } from '../constants/unitTypeRegistry';
import { BRIGADE_SUPPORT_AREA_KM } from '../constants/brigadeSupportStructure';
import {
  getBattalionComposition,
  expandBattalionComposition,
  BATTALION_TYPES,
  UNIT_TYPES  // v3.0 Phase 2: For doctrinal composition access
} from '../constants/unitTypes';
// v2.7.0: EA-001/EA-002 - Organic asset allocation
import {
  getRole1OrganicAssets,
  getRole2BasicOrganicAssets,
  getRole2EnhancedOrganicAssets,
  getRole3OrganicAssets,
  createFacilityAssetStructure,
  calculateEffectiveAssets
} from '../constants/evacuationAssetAllocation';

// v2.8.0: MTF-001 - Calculate MTF positions relative to parent HQ using doctrine offsets
import {
  calculateMTFPosition
} from '../constants/mtfDefaultOffsets';

// v2.9.0: MTF-002 - Import MTF capabilities from constants (no more hardcoded values)
import {
  ROLE_1_DEFINITION,
  ROLE_2_BASIC_DEFINITION,
  ROLE_2_ENHANCED_DEFINITION,
  ROLE_3_DEFINITION
} from '../constants/mtfCapabilities';

// MEDICAL BRIGADE CONSTANTS (v2.9.0 - Now uses mtfCapabilities.js)
// ============================================================================

// v2.9.0 MTF-002: Use imported constants instead of hardcoded values
const MEDICAL_BRIGADE_CONFIG = {
  has_combat_battalions: false,
  has_organic_role1: false,
  has_organic_role2_basic: false,
  provides_role2_enhanced: true,
  provides_role3: true,
  typical_components: [
    'Medical Battalion',
    'Surgical Hospital',
    'Evacuation Battalion',
    'Medical Supply Company',
    'Blood Bank Detachment'
  ],
  // v2.9.0: Role 2 Enhanced values from ROLE_2_ENHANCED_DEFINITION
  role2_enhanced: {
    beds: ROLE_2_ENHANCED_DEFINITION.holding_capacity.beds,                    // 40 (was 100)
    surgical_teams: ROLE_2_ENHANCED_DEFINITION.surgical_capability.teams,      // 4 (was 6)
    icu_beds: ROLE_2_ENHANCED_DEFINITION.holding_capacity.icu_beds,            // 10 (was 20)
    hold_hours: ROLE_2_ENHANCED_DEFINITION.holding_capacity.hold_time_hours,   // 72
    personnel: {
      medical_officers: 25,
      nurses: 60,
      medics: 40,
      support_staff: 30
    },
    blood_products: {
      prbc_units: 200,
      ffp_units: 200,
      platelet_units: 100,
      cryo_units: 50
    }
  },
  // v2.9.0: Role 3 values from ROLE_3_DEFINITION
  role3: {
    beds: ROLE_3_DEFINITION.holding_capacity.beds,                             // 150 (was 200)
    surgical_teams: ROLE_3_DEFINITION.surgical_capability.teams,               // 8 (same)
    icu_beds: ROLE_3_DEFINITION.holding_capacity.bed_breakdown.icu,            // 40 (same)
    ventilators: 32,
    hold_hours: ROLE_3_DEFINITION.holding_capacity.hold_time_hours,            // 168
    personnel: {
      medical_officers: 50,
      nurses: 120,
      medics: 60,
      support_staff: 50
    },
    blood_products: {
      prbc_units: 500,
      ffp_units: 500,
      platelet_units: 200,
      cryo_units: 100
    }
  },
  typical_personnel: 2500
};

// ============================================================================
// ARTILLERY BRIGADE TEMPLATE (v2.4.0)
// ============================================================================

const ARTILLERY_BRIGADE_TEMPLATE = {
  template: [
    { unit_type: 'ARTILLERY', count: 3 },   // 2026-10-24: was ARTILLERY x2 + ARTILLERY_SUPPORT x1, which the builder turned into ARTILLERY
    { unit_type: 'HQ', count: 1 }
  ],
  total_strength: 2400
};

// ============================================================================
// CSS BRIGADE TEMPLATES (v2.11.0 UT-001)
// Combat Service Support brigades with proper compositions
// ============================================================================

/**
 * SUPPORT Brigade (Sustainment/Multifunctional)
 * **US Army Terminology**
 * Provides supply, transportation, and maintenance support
 * Typically at Division or Corps level
 * 
 * NOTE: Different from LOGISTICS Brigade (NATO terminology)
 * - SUPPORT = US multifunctional sustainment (supply + transport + maintenance + logistics)
 * - LOGISTICS = NATO supply-focused (supply + transport only)
 * 
 * Display Label Recommendation: "Support Brigade (US)" or "Sustainment Brigade (US)"
 */
const SUPPORT_BRIGADE_TEMPLATE = {
  template: [
    { unit_type: 'SUPPLY', count: 1, strength: 500, mission: 'SUSTAINMENT' },
    { unit_type: 'TRANSPORT', count: 1, strength: 600, mission: 'SUSTAINMENT' },
    { unit_type: 'MAINTENANCE', count: 1, strength: 500, mission: 'SUSTAINMENT' },
    { unit_type: 'LOGISTICS', count: 1, strength: 400, mission: 'SUSTAINMENT' },
    { unit_type: 'HQ', count: 1, strength: 200, mission: 'COMMAND' }
  ],
  total_strength: 2200,
  position_feba_km: 35  // CSS brigades in rear area
};

/**
 * LOGISTICS Brigade (Supply-focused)
 * **NATO Terminology**
 * Focused on supply chain and distribution
 * Often at Corps level for theater logistics
 * 
 * NOTE: Different from SUPPORT Brigade (US terminology)
 * - LOGISTICS = NATO supply-focused (supply + transport only)
 * - SUPPORT = US multifunctional sustainment (includes maintenance + logistics)
 * 
 * Display Label Recommendation: "Logistics Brigade (NATO)"
 */
const LOGISTICS_BRIGADE_TEMPLATE = {
  template: [
    { unit_type: 'SUPPLY', count: 2, strength: 500, mission: 'SUSTAINMENT' },
    { unit_type: 'TRANSPORT', count: 1, strength: 600, mission: 'SUSTAINMENT' },
    { unit_type: 'HQ', count: 1, strength: 200, mission: 'COMMAND' }
  ],
  total_strength: 1800,
  position_feba_km: 40
};

/**
 * ENGINEER Brigade
 * Combat engineers, construction, and bridging capability
 * Critical for mobility and counter-mobility operations
 */
const ENGINEER_BRIGADE_TEMPLATE = {
  // 2026-10-24: the unit types ENGINEER, CONSTRUCTION and BRIDGE exist in no table the calculator reads, so a brigade built from the old
  // list stopped the calculation. The battalion type is ENGINEERS (BATTALION_TYPES); same composition as UNIT_TYPES.ENGINEER doctrinal_composition.
  template: [
    { unit_type: 'ENGINEERS', count: 3, strength: 700, mission: 'MOBILITY_SUPPORT' },
    { unit_type: 'HQ', count: 1, strength: 200, mission: 'COMMAND' }   // 2026-10-25: was logistics 400 + HQ 300 (2,800); the cards of brigadeCards.js are used
  ],
  total_strength: 2300,
  position_feba_km: 25  // Engineers closer for mobility support
};

/**
 * SIGNAL Brigade
 * Communications and network operations
 * Provides C4I infrastructure
 */
const SIGNAL_BRIGADE_TEMPLATE = {
  template: [
    { unit_type: 'SIGNAL', count: 3, strength: 400, mission: 'COMMUNICATIONS' },   // 2026-10-24: was SIGNAL x2 + NETWORK x1 (no unit type NETWORK)
    { unit_type: 'HQ', count: 1, strength: 200, mission: 'COMMAND' }
  ],
  total_strength: 1400,
  position_feba_km: 30
};

/**
 * Map of CSS brigade types to their templates
 */
const CSS_BRIGADE_TEMPLATES = {
  SUPPORT: SUPPORT_BRIGADE_TEMPLATE,
  LOGISTICS: LOGISTICS_BRIGADE_TEMPLATE,
  ENGINEERS: ENGINEER_BRIGADE_TEMPLATE,   // 2026-10-24: one key (ENGINEER was a second entry for the same template)
  SIGNAL: SIGNAL_BRIGADE_TEMPLATE
};

// ============================================================================
// v2.5.0: UNIT TYPE TO ASSET TYPE MAPPING
// ============================================================================

/**
 * Map battalion unit types to organic asset allocation profiles
 * Used by Phase 2 facility models for correct asset initialization
 * 
 * v2.11.0: Added CSS battalion types with LIGHT profile
 */
const UNIT_TYPE_TO_ASSET_PROFILE = {
  // -------------------------------------------------------------------------
  // Combat / Maneuver — STANDARD Role 1 asset allocation
  // BUG-185 FIX (March 24, 2026): Added RECONNAISSANCE (replaces stale CAVALRY
  //   key per DOCTRINAL-005 / unitTypes.js v2.11), AIRBORNE, AIR_ASSAULT,
  //   MOUNTAIN. All correctly map to STANDARD (infantry-scale Role 1 kit).
  //   Legacy CAVALRY key kept for any data persisted before unitTypes.js v2.11.
  // -------------------------------------------------------------------------
  INFANTRY:       'STANDARD',
  MECHANIZED:     'STANDARD',
  ARMOR:          'HEAVY',
  RECONNAISSANCE: 'STANDARD',  // DOCTRINAL-005: renamed from CAVALRY (v2.11)
  AIRBORNE:       'STANDARD',  // Light infantry — standard Role 1 kit, air-portable
  AIR_ASSAULT:    'STANDARD',  // Helicopter-inserted infantry — same footprint as AIRBORNE
  MOUNTAIN:       'STANDARD',  // Mountain infantry — standard Role 1 kit
  SPECIAL_FORCES: 'STANDARD',  // 2026-10-24: Ranger and special forces battalion, as INFANTRY

  // -------------------------------------------------------------------------
  // Artillery / Air Defense
  // -------------------------------------------------------------------------
  ARTILLERY:         'ARTILLERY',
  AIR_DEFENSE:       'STANDARD',

  // -------------------------------------------------------------------------
  // Aviation — LIGHT (smaller crew, co-located with flight surgeon)
  // BUG-185 FIX: was missing → fell back to 'STANDARD'. LIGHT is correct:
  //   aviation battalions operate from airfields, not dismounted forward positions.
  //   Mirrors BRIGADE_TYPE_TO_ASSET_PROFILE.AVIATION = 'LIGHT_BRIGADE'.
  // -------------------------------------------------------------------------
  AVIATION:       'LIGHT',
  ATTACK_AVIATION: 'LIGHT',    // 2026-10-24: as AVIATION

  // -------------------------------------------------------------------------
  // Engineers — STANDARD (operate close to combat)
  // v2.6.1: Both ENGINEERS (correct) and ENGINEER (legacy) keys retained
  // -------------------------------------------------------------------------
  ENGINEERS:      'STANDARD',

  // -------------------------------------------------------------------------
  // CSS battalions — LIGHT Role 1 allocation
  // BUG-184 FIX (March 24, 2026): Added TRANSPORT key. Previous key was
  //   TRANSPORTATION — mismatch with BATTALION_TYPES which uses TRANSPORT.
  //   getAssetProfile('TRANSPORT') was falling back to 'STANDARD', over-
  //   allocating Role 1 assets for transport battalions.
  //   Legacy TRANSPORTATION key retained for persisted data.
  // -------------------------------------------------------------------------
  SUPPLY:         'LIGHT',
  MILITARY_POLICE: 'LIGHT',    // 2026-10-24
  TRANSPORT:      'LIGHT',       // BUG-184 FIX: correct key (was TRANSPORTATION)
  LOGISTICS:      'LIGHT',
  MAINTENANCE:    'LIGHT',

  // -------------------------------------------------------------------------
  // No Role 1 — these types skip Role 1 creation entirely.
  // needs_role1: false in BATTALION_TYPES — null here documents the intent.
  // -------------------------------------------------------------------------
  HQ:              null,
  CBRN_DEFENSE:    null,   // 2026-10-24: no Role 1 of its own (the supported unit's), like SIGNAL
  CIVIL_AFFAIRS:   null,
  PSYCHOLOGICAL_OPS: null,
  SIGNAL:          null,  // Small staff, co-located with parent HQ
  MEDICAL:         null,  // ARE the medical providers — no separate Role 1
  MEDICAL_SUPPORT: null   // Medical logistics only — no treatment capability
};

/**
 * Map brigade types to Role 2B asset profiles
 * v2.11.0: Added CSS brigade types
 */
const BRIGADE_TYPE_TO_ASSET_PROFILE = {
  // Combat brigades
  INFANTRY:    'STANDARD',
  MECHANIZED:  'STANDARD',
  ARMOR:       'HEAVY_BRIGADE',
  HEAVY:       'HEAVY_BRIGADE',     // v3.2.2 FIX: was missing → fell back to STANDARD
  AIRBORNE:    'LIGHT_BRIGADE',
  AIR_ASSAULT: 'LIGHT_BRIGADE',
  ARTILLERY:   'ARTILLERY_BRIGADE',
  STRYKER:     'STANDARD',
  MOUNTAIN:    'LIGHT_BRIGADE',     // v3.2.2 FIX: was missing → fell back to STANDARD

  // v3.2.2 FIX: previously missing — silent STANDARD fallback
  RECONNAISSANCE: 'STANDARD',       // Reconnaissance — similar footprint to infantry (was the key CAVALRY)
  AIR_DEFENSE: 'LIGHT_BRIGADE',     // AD battalion-centric; lighter R2 footprint
  AVIATION:    'LIGHT_BRIGADE',     // Aviation brigade; medical co-located with ALAT assets

  // Medical support — medical brigades provide R2+, they are not consumers
  MEDICAL:     'LIGHT_BRIGADE',     // v3.2.2 FIX: was missing → fell back to STANDARD
  HQ:          'LIGHT_BRIGADE',     // v3.2.2 FIX: HQ elements; minimal organic medical

  // v2.11.0 UT-001: CSS brigades - lighter medical footprint
  SUPPORT:     'LIGHT_BRIGADE',
  LOGISTICS:   'LIGHT_BRIGADE',
  ENGINEERS:   'STANDARD',          // Engineers closer to combat, need more assets
  SIGNAL:      'LIGHT_BRIGADE'
};

/**
 * v2.11.0: Check if a brigade type is a CSS (Combat Service Support) type
 */
const isCSS_BrigadeType = (unitType) => {
  return CSS_BRIGADE_TEMPLATES.hasOwnProperty(unitType);
};

export class ForceBuilder {
  
  // ============================================================================
  // HELPER: Check if unit type is Medical Brigade
  // ============================================================================
  
  static isMedicalBrigade(unitType) {
    return unitType === 'MEDICAL';
  }
  
  // ============================================================================
  // v2.10.0: HELPER - Determine if battalion type needs Role 1 (FB-003 FIX)
  // ============================================================================
  
  /**
   * Determine if a battalion type needs its own Role 1 medical facility
   * 
   * v2.10.0 FB-003 FIX: Removed LOGISTICS from exclusion list
   * - LOGISTICS battalions have 500+ personnel who can be injured
   * - Transport convoys get ambushed, depot workers have accidents
   * - These soldiers need Role 1 medical care
   * 
   * v2.11.0 UT-001: All CSS battalion types get Role 1:
   * - SUPPLY, TRANSPORTATION, MAINTENANCE, CONSTRUCTION, BRIDGE, NETWORK, LOGISTICS
   * - These units have personnel who can be injured in rear area operations
   * 
   * Only these types are excluded:
   * - HQ: Small staff (~50), co-located with units that have Role 1
   * - SIGNAL: Small staff (~100), co-located with other units  
   * - MEDICAL: Personnel treated in the facilities they operate
   * 
   * @param {string} unitType - Battalion unit type
   * @returns {boolean} True if battalion needs Role 1
   */
  static battalionNeedsRole1(unitType) {
    // BUG-181 FIX: hardcoded ['HQ', 'SIGNAL', 'MEDICAL'] list was out of sync with
    // unitTypes.js. The correct per-type value is now needs_role1 on every entry in
    // BATTALION_TYPES (added in unitTypes.js v2.11). Read it directly.
    // Default true preserves the previous behaviour for any unknown type.
    return BATTALION_TYPES[unitType]?.needs_role1 ?? true;
  }
  
  // ============================================================================
  // v2.5.0: GET ASSET PROFILE FOR UNIT TYPE
  // ============================================================================
  
  /**
   * Get the organic asset profile key for a given unit type
   * 
   * @param {string} unitType - Battalion unit type
   * @returns {string|null} Asset profile key or null if no Role 1
   */
  static getAssetProfile(unitType) {
    // 2026-10-24 (naming audit step 4): an unknown type or a type without a row throws; a row with null (no Role 1) gives STANDARD as before
    return lookupByUnitType(UNIT_TYPE_TO_ASSET_PROFILE, unitType, 'ForceBuilder.getAssetProfile') || 'STANDARD';
  }
  
  /**
   * Get the organic asset profile key for a brigade type
   * 
   * @param {string} brigadeType - Brigade unit type
   * @returns {string} Asset profile key
   */
  static getBrigadeAssetProfile(brigadeType) {
    return lookupByUnitType(BRIGADE_TYPE_TO_ASSET_PROFILE, brigadeType, 'ForceBuilder.getBrigadeAssetProfile') || 'STANDARD';
  }
  
  // ============================================================================
  // v3.0 PHASE 2: DOCTRINAL COMPOSITION GENERATION (NO FALLBACKS)
  // ============================================================================
  
  /**
   * Generate sub_units array from doctrinal composition
   * 
   * v3.0 Phase 2: Auto-generates brigade sub-units from unitTypes.js doctrinal templates
   * NO FALLBACKS: Throws errors if composition data missing or invalid
   * 
   * @param {string} unitType - Brigade unit type (e.g., 'MECHANIZED', 'ARMOR')
   * @param {string} parentName - Parent unit name for sub-unit naming
   * @param {string} parentId - Parent unit ID for linking
   * @param {string} echelon - 'brigade' or 'battalion' composition level
   * @returns {Array} Array of sub-unit objects with complete data
   * @throws {Error} If doctrinal composition missing or incomplete
   */
  static generateSubUnitsFromDoctrine(unitType, parentName, parentId, echelon = 'brigade') {
    // REQUIRE unit type
    if (!unitType) {
      throw new Error('generateSubUnitsFromDoctrine: unitType is required');
    }
    
    // Get unit type definition
    const unitTypeDefinition = UNIT_TYPES[unitType];
    if (!unitTypeDefinition) {
      throw new Error(`generateSubUnitsFromDoctrine: Unknown unit type '${unitType}'`);
    }
    
    // REQUIRE doctrinal composition
    const doctrinalComposition = unitTypeDefinition.doctrinal_composition;
    if (!doctrinalComposition) {
      throw new Error(`generateSubUnitsFromDoctrine: Unit type '${unitType}' missing doctrinal_composition field`);
    }
    
    // REQUIRE echelon-specific composition
    const composition = doctrinalComposition[echelon];
    if (!composition) {
      throw new Error(`generateSubUnitsFromDoctrine: Unit type '${unitType}' missing ${echelon} composition`);
    }
    
    // REQUIRE sub_units array
    if (!composition.sub_units || !Array.isArray(composition.sub_units)) {
      throw new Error(`generateSubUnitsFromDoctrine: Unit type '${unitType}' ${echelon} composition missing sub_units array`);
    }
    
    const subUnits = [];
    let letterIndex = 0;  // For company letter naming (A, B, C...)
    
    // Generate sub-units from templates
    for (const template of composition.sub_units) {
      // REQUIRE all template fields
      if (!template.type) {
        throw new Error(`generateSubUnitsFromDoctrine: Sub-unit template missing 'type' field for ${unitType}`);
      }
      if (!template.unit_type) {
        throw new Error(`generateSubUnitsFromDoctrine: Sub-unit template missing 'unit_type' field for ${unitType}`);
      }
      if (!template.count || template.count < 1) {
        throw new Error(`generateSubUnitsFromDoctrine: Sub-unit template missing or invalid 'count' field for ${unitType}`);
      }
      if (!template.strength_each || template.strength_each < 1) {
        throw new Error(`generateSubUnitsFromDoctrine: Sub-unit template missing or invalid 'strength_each' field for ${unitType}`);
      }
      if (!template.name_pattern) {
        throw new Error(`generateSubUnitsFromDoctrine: Sub-unit template missing 'name_pattern' field for ${unitType}`);
      }
      
      // Generate multiple units based on count
      for (let i = 1; i <= template.count; i++) {
        let subUnitName = template.name_pattern;
        
        // Replace name pattern variables
        subUnitName = subUnitName.replace('{parent}', parentName);
        subUnitName = subUnitName.replace('{index}', i.toString());
        
        // For {letter}, use A, B, C... sequence
        if (subUnitName.includes('{letter}')) {
          const letters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
          subUnitName = subUnitName.replace('{letter}', letters[letterIndex % 26]);
          letterIndex++;
        }
        
        const subUnit = {
          id: `${template.type.toLowerCase()}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
          type: template.type,
          unit_type: template.unit_type,
          strength: template.strength_each,
          name: subUnitName,
          parent_id: parentId,
          notes: template.notes || '',
          composition_source: 'doctrinal'  // Mark as auto-generated
        };
        
        subUnits.push(subUnit);
      }
    }
    
    return subUnits;
  }
  
  /**
   * Validate brigade data completeness
   * 
   * v3.0 Phase 2: NO FALLBACKS - Throws errors if data incomplete
   * Ensures brigade has all required fields for casualty calculations
   * 
   * @param {Object} brigade - Brigade object to validate
   * @throws {Error} If brigade data incomplete or invalid
   */

  static validateBrigadeData(brigade) {
    if (!brigade) {
      throw new Error('validateBrigadeData: brigade object is null or undefined');
    }
    
    // REQUIRE basic fields
    if (!brigade.name) {
      throw new Error(`validateBrigadeData: Brigade missing 'name' field`);
    }
    if (!brigade.unit_type) {
      throw new Error(`validateBrigadeData: Brigade '${brigade.name}' missing 'unit_type' field`);
    }
    if (!brigade.strength || brigade.strength < 1) {
      throw new Error(`validateBrigadeData: Brigade '${brigade.name}' missing or invalid 'strength' field`);
    }
    
    // REQUIRE sub_units array (critical for v3.0 calculations)
    if (!brigade.sub_units || !Array.isArray(brigade.sub_units)) {
      throw new Error(`validateBrigadeData: Brigade '${brigade.name}' missing 'sub_units' array - required for v3.0 casualty calculations`);
    }
    
    if (brigade.sub_units.length === 0) {
      throw new Error(`validateBrigadeData: Brigade '${brigade.name}' has empty 'sub_units' array - composition generation failed`);
    }
    
    // Validate each sub-unit
    let totalStrengthFromSubUnits = 0;
    for (let i = 0; i < brigade.sub_units.length; i++) {
      const subUnit = brigade.sub_units[i];
      
      if (!subUnit.unit_type) {
        throw new Error(`validateBrigadeData: Brigade '${brigade.name}' sub-unit ${i} missing 'unit_type' field`);
      }
      if (!subUnit.strength || subUnit.strength < 1) {
        throw new Error(`validateBrigadeData: Brigade '${brigade.name}' sub-unit ${i} (${subUnit.name || 'unnamed'}) missing or invalid 'strength' field`);
      }
      if (!subUnit.name) {
        throw new Error(`validateBrigadeData: Brigade '${brigade.name}' sub-unit ${i} missing 'name' field`);
      }
      
      totalStrengthFromSubUnits += subUnit.strength;
    }
    
    // Verify total strength matches sum of sub-units
    if (Math.abs(brigade.strength - totalStrengthFromSubUnits) > 1) {
      throw new Error(`validateBrigadeData: Brigade '${brigade.name}' strength mismatch: brigade.strength=${brigade.strength} but sum of sub_units=${totalStrengthFromSubUnits}`);
    }
    
  }
  
  /**
   * Validate battalion data completeness
   * 
   * v3.0 Phase 2: NO FALLBACKS - Throws errors if data incomplete
   * 
   * @param {Object} battalion - Battalion object to validate
   * @throws {Error} If battalion data incomplete or invalid
   */
  static validateBattalionData(battalion) {
    if (!battalion) {
      throw new Error('validateBattalionData: battalion object is null or undefined');
    }
    
    // REQUIRE basic fields
    if (!battalion.name) {
      throw new Error(`validateBattalionData: Battalion missing 'name' field`);
    }
    if (!battalion.unit_type) {
      throw new Error(`validateBattalionData: Battalion '${battalion.name}' missing 'unit_type' field`);
    }
    if (!battalion.strength || battalion.strength < 1) {
      throw new Error(`validateBattalionData: Battalion '${battalion.name}' missing or invalid 'strength' field`);
    }
    
    // Note: Battalions don't have sub_units in v3.0 Phase 2 (future enhancement)
    // This validation is for future-proofing when battalion-level composition is added
    
  }
  
  // ============================================================================
  // BATTALION BUILDER (ENHANCED v2.5.0 - Evacuation Asset Integration)
  // ============================================================================
  
  /**
   * Build a complete battalion with Role 1 facility
   * 
   * v2.5.0 ENHANCEMENT: Passes unit_type to Role 1 for organic asset allocation
   * v2.4.0 FIX: Role 1 creation based on battalionNeedsRole1() not has_frontage
   * 
   * @param {Object} config - Battalion configuration
   * @returns {Object} Battalion with linked Role 1 facility
   */
  static buildBattalion(config) {
    const battalionId = config.id || `bn_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    
    const unitType = normalizeUnitTypeKey(config.unit_type) || 'MECHANIZED';
    const unitTypeConfig = BATTALION_TYPES[unitType] || BATTALION_TYPES.MECHANIZED;
    
    // v3.0.4 FIX (Feb 5, 2026): No fallback - nationalStandard must be provided
    const nationalStandard = config.nationalStandard;
    if (!nationalStandard) {
      throw new Error(`buildBattalion: Missing nationalStandard for battalion "${config.name}"`);
    }
    
    // v2.4 Phase 2.1: Get national standards for fallbacks
    const standard = NATIONAL_STANDARDS[nationalStandard];
    
    // v3.0.6: Calculate breakdown from nationalStandards.js v2.6 breakdown_ratios
    const hqPersonnel = standard?.battalion?.hq_personnel || 100;  // Battalion HQ only
    const breakdownRatios = standard?.battalion?.breakdown_ratios;
    const breakdown = breakdownRatios ? {
      command: Math.round(hqPersonnel * breakdownRatios.command),
      support: Math.round(hqPersonnel * breakdownRatios.support),
      combat: 0  // Combat comes from line companies, not HQ
    } : null;
    
    let strength = config.strength;
    if (!strength) {
      // BUG-176 FIX (final): Strength resolution is unit-type-aware.
      //
      // ARCHITECTURE NOTE (v3.3.3):
      // Brigade/division/corps strength is top-down (national standard → BUG-113 scaling).
      // BATTALION echelon is the only path where per-type strength matters at build time.
      // True bottom-up aggregation (ARCH-010) deferred to v4.
      //
      // MANEUVER types (INFANTRY, MECHANIZED, ARMOR, RECONNAISSANCE, AIRBORNE, AIR_ASSAULT,
      // MOUNTAIN, HEAVY, STRYKER, AVIATION): national standard battalion strength is
      // authoritative — Romanian mechanized=600, German=1000, French=950.
      //
      // CS/CSS types (ARTILLERY, ENGINEERS, AIR_DEFENSE, SIGNAL, LOGISTICS, MAINTENANCE,
      // MEDICAL, TRANSPORT, SUPPLY, MEDICAL_SUPPORT): doctrinal typical_strength is
      // authoritative — these don't vary significantly by nation (function-constrained,
      // not doctrine-constrained). Per-nation values come in v4 via ARCH-010.
      const MANEUVER_TYPES = [
        'INFANTRY', 'MECHANIZED', 'ARMOR', 'RECONNAISSANCE',
        'AIRBORNE', 'AIR_ASSAULT', 'MOUNTAIN', 'HEAVY', 'STRYKER', 'AVIATION'
      ];
      if (MANEUVER_TYPES.includes(unitType)) {
        strength = getDefaultStrength(nationalStandard, 'BATTALION')
                || unitTypeConfig.typical_strength || 800;
      } else {
        // CS/CSS: doctrinal typical_strength wins; national standard is fallback only
        strength = unitTypeConfig.typical_strength
                || getDefaultStrength(nationalStandard, 'BATTALION') || 800;
      }
    }

    // Phase 2.1 Step 7: Check for manual HQ override from Tab2
    const override = config.scenario?.operation_parameters?.hq_override_config;
    let position = config.position_feba_km;
    if (override?.enabled && override.battalion_hq_km !== null && override.battalion_hq_km !== undefined) {
      position = override.battalion_hq_km;
    } else if (position === undefined) {
      // v2.4 Phase 2.1: Use nationalStandards.js v2.4 for battalion HQ position fallback
      position = unitTypeConfig.typical_position_km !== undefined 
        ? unitTypeConfig.typical_position_km 
        : (standard?.battalion?.hq_position_feba_km || 8);  // National standard fallback
    }
    
    let isFrontline = config.is_frontline;
    if (isFrontline === undefined) {
      isFrontline = unitTypeConfig.has_frontage || false;
    }
    
    const battalion = new Battalion({
      id: battalionId,
      name: config.name,
      battalion_number: config.battalion_number || 1,
      unit_type: unitType,
      operational_state: config.operational_state,
      nationalStandard: nationalStandard,
      strength: strength,
      breakdown: breakdown,  // v3.0.6: Command/support breakdown
      position_feba_km: position,
      mission: config.mission || 'SUPPORTING',
      is_frontline: isFrontline,
      parent_brigade_id: config.parent_brigade_id,
      parent_division_id: config.parent_division_id,
      position_anchor: config.position_anchor || null
    });
    
    const skipRole1 = config.skip_role1 || !this.battalionNeedsRole1(unitType);
    
    if (skipRole1) {
      
      // v3.0 PHASE 2: Validate battalion data before return
      try {
        this.validateBattalionData(battalion);
      } catch (error) {
        throw new Error(`buildBattalion: Validation failed for battalion '${config.name}': ${error.message}`);
      }
      
      return {
        battalion: battalion,
        role1: null,
        summary: battalion.getSummary ? battalion.getSummary() : { id: battalion.id, name: battalion.name }
      };
    }
    
    
    // v2.5.0: Pass unit_type for organic asset allocation
    const assetProfile = this.getAssetProfile(unitType);
    
    // v2.7.0 EA-001: Get organic assets for this unit type
    const organicAssets = getRole1OrganicAssets(assetProfile, nationalStandard);
    const assetStructure = createFacilityAssetStructure(organicAssets);
    
    const role1 = new Role1Facility({
      facility_id: battalion.generateFacilityId ? battalion.generateFacilityId() : `role1_${battalionId}`,
      facility_name: battalion.generateFacilityName ? battalion.generateFacilityName() : `${config.name} Aid Station`,
      parent_unit_id: battalion.id,
      parent_battalion_id: battalion.id,
      // v2.8.0 MTF-001: Position = Battalion HQ + Role 1 offset (typically +3km behind HQ)
      position_feba_km: calculateMTFPosition(battalion.position_feba_km, 'ROLE1'),
      // v2.9.0 MTF-002: Beds from doctrine constant (6, was hardcoded 10)
      beds: ROLE_1_DEFINITION.holding_capacity.beds,
      // v2.5.0: Pass unit type for organic asset determination
      unit_type: assetProfile,
      // v2.7.0 EA-001/EA-002: Organic asset allocation with tracking structure
      organic_assets: assetStructure.organic_assets,
      attached_assets: assetStructure.attached_assets,
      detached_assets: assetStructure.detached_assets,
      effective_assets: calculateEffectiveAssets(assetStructure),
      // v2.5.0: Hierarchy for display
      hierarchy: {
        corps: config.parent_corps_name || null,
        division: config.parent_division_name || null,
        brigade: config.parent_brigade_name || null,
        battalion: config.name
      }
    });
    
    // Link facility to battalion
    if (battalion.linkRole1Facility) {
      battalion.linkRole1Facility(role1);
    }
    
    // Set default personnel
    if (role1.setPersonnel) {
      role1.setPersonnel({
        medical_officers: 2,
        nurses: 4,
        medics: 12,
        support_staff: 2
      });
    }
    
    // v2.5.0: Set default supplies
    if (role1.resupply) {
      role1.resupply({
        tourniquets: 50,
        txa_doses: 30,
        trauma_kits: 100,
        iv_fluids: 50,
        bandages: 500
      });
    }
    
    // v3.0 PHASE 2: Validate battalion data before return
    try {
      this.validateBattalionData(battalion);
    } catch (error) {
      throw new Error(`buildBattalion: Validation failed for battalion '${config.name}': ${error.message}`);
    }
    
    return {
      battalion: battalion,
      role1: role1,
      summary: battalion.getSummary ? battalion.getSummary() : { id: battalion.id, name: battalion.name }
    };
  }
  
  // ============================================================================
  // MEDICAL BRIGADE BUILDER (v2.4.0 + v2.5.0 Enhancements)
  // ============================================================================
  
  static buildMedicalBrigade(config) {
    const brigadeId = config.id || `medbrig_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    
    // v3.0.4 FIX (Feb 5, 2026): No fallback - nationalStandard must be provided
    const nationalStandard = config.nationalStandard;
    if (!nationalStandard) {
      throw new Error(`buildMedicalBrigade: Missing nationalStandard for medical brigade "${config.name}"`);
    }
    
    
    const brigade = new Brigade({
      id: brigadeId,
      name: config.name,
      brigade_number: config.brigade_number || 1,
      nationalStandard: nationalStandard,
      strength: config.strength || MEDICAL_BRIGADE_CONFIG.typical_personnel,
      // v2.8.0 MTF-001: Medical Brigade typically positioned in division/corps rear area
      // Use provided position or default to 50km (division support area)
      position_feba_km: config.position_feba_km !== undefined ? config.position_feba_km : 50,
      parent_division_id: config.parent_division_id
    });
    
    brigade.unit_type = 'MEDICAL';
    brigade.is_medical_provider = true;
    brigade.has_organic_role1 = false;
    brigade.has_organic_role2_basic = false;
    
    // v2.7.0 EA-001: Get organic assets for Role 2E
    const role2eOrganicAssets = getRole2EnhancedOrganicAssets(nationalStandard);
    const role2eAssetStructure = createFacilityAssetStructure(role2eOrganicAssets);
    
    // Create Role 2 Enhanced Facility
    const role2eConfig = MEDICAL_BRIGADE_CONFIG.role2_enhanced;
    // v2.8.0 MTF-001: Position = Brigade HQ + Role 2E offset
    const role2ePosition = calculateMTFPosition(brigade.position_feba_km, 'ROLE2_ENHANCED');
    const role2Enhanced = new Role2EnhancedFacility({
      facility_id: `role2e_${brigadeId}`,
      facility_name: `${config.name} Field Hospital`,
      parent_unit_id: brigade.id,
      parent_brigade_id: brigade.id,
      position_feba_km: role2ePosition,
      beds: role2eConfig.beds,
      surgical_teams: role2eConfig.surgical_teams,
      icu_beds: role2eConfig.icu_beds,
      is_from_medical_brigade: true,
      // v2.5.0: Division type for asset allocation
      division_type: 'STANDARD',
      // v2.7.0 EA-001/EA-002: Organic asset allocation with tracking structure
      organic_assets: role2eAssetStructure.organic_assets,
      attached_assets: role2eAssetStructure.attached_assets,
      detached_assets: role2eAssetStructure.detached_assets,
      effective_assets: calculateEffectiveAssets(role2eAssetStructure),
      // v2.5.0: Hierarchy
      hierarchy: {
        corps: config.parent_corps_name || null,
        division: config.parent_division_name || null,
        brigade: config.name,
        battalion: null
      }
    });
    
    if (role2Enhanced.setPersonnel) {
      role2Enhanced.setPersonnel(role2eConfig.personnel);
    }
    
    if (role2Enhanced.resupplyBlood) {
      role2Enhanced.resupplyBlood(role2eConfig.blood_products);
    }
    
    // v2.7.0 EA-001: Get organic assets for Role 3
    const role3OrganicAssets = getRole3OrganicAssets(nationalStandard);
    const role3AssetStructure = createFacilityAssetStructure(role3OrganicAssets);
    
    // Create Role 3 Facility
    const role3Config = MEDICAL_BRIGADE_CONFIG.role3;
    // v2.8.0 MTF-001: Position = Brigade HQ + Role 3 offset
    const role3Position = calculateMTFPosition(brigade.position_feba_km, 'ROLE3');
    const role3 = new Role3Facility({
      facility_id: `role3_${brigadeId}`,
      facility_name: `${config.name} Combat Support Hospital`,
      parent_unit_id: brigade.id,
      parent_brigade_id: brigade.id,
      position_feba_km: role3Position,
      beds: role3Config.beds,
      surgical_teams: role3Config.surgical_teams,
      icu_beds: role3Config.icu_beds,
      ventilators: role3Config.ventilators,
      is_from_medical_brigade: true,
      // v2.5.0: National standard for asset allocation
      national_standard: nationalStandard === 'ROMANIAN' ? 'ROMANIAN_CORPS' : 'STANDARD',
      // v2.7.0 EA-001/EA-002: Organic asset allocation with tracking structure
      organic_assets: role3AssetStructure.organic_assets,
      attached_assets: role3AssetStructure.attached_assets,
      detached_assets: role3AssetStructure.detached_assets,
      effective_assets: calculateEffectiveAssets(role3AssetStructure),
      hierarchy: {
        corps: config.parent_corps_name || null,
        division: config.parent_division_name || null,
        brigade: config.name,
        battalion: null
      }
    });
    
    if (role3.setPersonnel) {
      role3.setPersonnel(role3Config.personnel);
    }
    
    if (role3.resupplyBlood) {
      role3.resupplyBlood(role3Config.blood_products);
    }
    
    
    brigade.provides = {
      role2_enhanced: MEDICAL_BRIGADE_CONFIG.provides_role2_enhanced,
      role3: MEDICAL_BRIGADE_CONFIG.provides_role3,
      role2_enhanced_beds: role2eConfig.beds,
      role3_beds: role3Config.beds,
      surgical_teams_role2e: role2eConfig.surgical_teams,
      surgical_teams_role3: role3Config.surgical_teams
    };
    
    brigade.medical_components = MEDICAL_BRIGADE_CONFIG.typical_components;

    // 2026-10-25 (Dr. Tatar): the medical brigade has cards too (hospital center, multifunctional medical battalions, headquarters): the people
    // exposed to casualties, in the rear and protected (casualty modifier 0.15). No Role 1 of their own; the Role 2 Enhanced and Role 3 stay as built above.
    const medicalCards = BRIGADE_CARDS_ON ? brigadeCardsFor('MEDICAL') : null;
    const medicalBattalions = [];
    if (medicalCards) {
      medicalCards.forEach((card, i) => {
        const bnTypeConfig = BATTALION_TYPES[card.unit_type] || BATTALION_TYPES.MEDICAL;
        const bnPosition = card.position === 'BRIGADE' ? brigade.position_feba_km : bnTypeConfig.typical_position_km;
        const built = this.buildBattalion({
          name: card.label ? `${config.name} ${card.label}` : this._generateBattalionName(config.name, i + 1, card.unit_type),
          battalion_number: i + 1,
          unit_type: card.unit_type,
          nationalStandard: nationalStandard,
          strength: card.strength,
          position_feba_km: bnPosition,
          mission: card.mission || 'SUSTAINMENT',
          is_frontline: false,
          parent_brigade_id: brigade.id,
          parent_brigade_name: config.name,
          parent_division_id: config.parent_division_id,
          parent_division_name: config.parent_division_name,
          parent_corps_name: config.parent_corps_name,
          position_anchor: card.position === 'BRIGADE' ? 'BRIGADE' : null,
          skip_role1: true
        });
        if (brigade.addBattalion) brigade.addBattalion(built.battalion);
        medicalBattalions.push(built.battalion);
      });
      brigade.sub_units = this.subUnitsFromBattalions(brigade.id, medicalBattalions);
      brigade.composition_source = 'cards';
      brigade.strength = brigadeCardsTotal(medicalCards);
      brigade.current_strength = brigade.strength;
    }
    
    return {
      brigade: brigade,
      battalions: medicalBattalions,
      role2: null,
      role1_facilities: [],
      role2_enhanced: role2Enhanced,
      role3: role3,
      is_medical_brigade: true,
      provides: brigade.provides,
      summary: {
        ...(brigade.getSummary ? brigade.getSummary() : { id: brigade.id, name: brigade.name }),
        is_medical_brigade: true,
        provides_role2_enhanced: true,
        provides_role3: true,
        role2_enhanced_beds: role2eConfig.beds,
        role3_beds: role3Config.beds,
        total_beds: role2eConfig.beds + role3Config.beds
      }
    };
  }
  
  // ============================================================================
  // v2.4.0: HELPER - Get Artillery Brigade Composition
  // ============================================================================
  
  static getArtilleryBattalionComposition(requestedBattalions = null) {
    const template = ARTILLERY_BRIGADE_TEMPLATE.template;
    let expanded = [];
    
    let bnNumber = 1;
    for (const item of template) {
      for (let i = 0; i < item.count; i++) {
        const battalionSpec = {
          battalion_number: bnNumber,
          unit_type: item.unit_type,
          strength: item.unit_type === 'HQ' ? 200 : 700,
          is_frontline: false,
          mission: item.unit_type === 'HQ' ? 'COMMAND' : 'FIRE_SUPPORT'
        };
        expanded.push(battalionSpec);
        bnNumber++;
      }
    }
    
    if (requestedBattalions && requestedBattalions !== expanded.length) {
      if (requestedBattalions > expanded.length) {
        while (expanded.length < requestedBattalions) {
          expanded.push({
            battalion_number: expanded.length + 1,
            unit_type: 'ARTILLERY',
            strength: 700,
            is_frontline: false,
            mission: 'FIRE_SUPPORT'
          });
        }
      } else {
        expanded = expanded.slice(0, requestedBattalions);
      }
    }
    
    return expanded;
  }
  
  // ============================================================================
  // v2.11.0 UT-001: HELPER - Get CSS Brigade Composition
  // ============================================================================
  
  /**
   * Get battalion composition for CSS (Combat Service Support) brigades
   * Uses templates from CSS_BRIGADE_TEMPLATES
   * 
   * @param {string} brigadeType - CSS brigade type (SUPPORT, LOGISTICS, ENGINEER, SIGNAL)
   * @param {number|null} requestedBattalions - Optional override for battalion count
   * @returns {Array} Array of battalion specifications
   */
  
static getCSSBattalionComposition(brigadeType, requestedBattalions = null) {
    const template = CSS_BRIGADE_TEMPLATES[brigadeType];
    if (!template) {
      return this.getCSSBattalionComposition('SUPPORT', requestedBattalions);
    }
    
    let expanded = [];
    let bnNumber = 1;
    
    for (const item of template.template) {
      for (let i = 0; i < item.count; i++) {
        const battalionSpec = {
          battalion_number: bnNumber,
          unit_type: item.unit_type,
          strength: item.strength || 500,
          is_frontline: false,  // CSS units not on front line
          mission: item.mission || 'SUSTAINMENT'
        };
        expanded.push(battalionSpec);
        bnNumber++;
      }
    }
    
    // Adjust battalion count if specified
    if (requestedBattalions && requestedBattalions !== expanded.length) {
      const primaryType = this._getCSSPrimaryBattalionType(brigadeType);
      
      if (requestedBattalions > expanded.length) {
        while (expanded.length < requestedBattalions) {
          expanded.push({
            battalion_number: expanded.length + 1,
            unit_type: primaryType,
            strength: 500,
            is_frontline: false,
            mission: 'SUSTAINMENT'
          });
        }
      } else {
        expanded = expanded.slice(0, requestedBattalions);
      }
    }
    
    return expanded;
  }
  
  /**
   * Get the primary battalion type for a CSS brigade (for expansion)
   * @private
   */
  static _getCSSPrimaryBattalionType(brigadeType) {
    const primaryTypes = {
      SUPPORT: 'LOGISTICS',
      LOGISTICS: 'SUPPLY',
      ENGINEERS: 'ENGINEERS',
      SIGNAL: 'SIGNAL'
    };
    return primaryTypes[brigadeType] || 'LOGISTICS';
  }
  
  // ============================================================================
  // v3.3.0: HELPER — regenerate scaled sub_units after a brigade type change
  // ============================================================================

  /**
   * Regenerate sub_units for a brigade whose unit_type was changed in TacticalEditor
   * (BUG-131 fix). Tab1 handleSaveUnit does a shallow spread of the edited unit —
   * the sub_units from the previous type survive and carry the wrong strength into
   * the calculator, Tab 5, and Tab 6.
   *
   * Calls generateSubUnitsFromDoctrine() for the new type then applies the same
   * BUG-113 proportional scaling as buildBrigade().
   *
   * Returns null for types that do not use doctrinal sub_units (ARTILLERY, CSS,
   * MEDICAL) — the caller should skip sub_units replacement in those cases.
   *
   * @param {string} unitType        — new brigade type (e.g. 'HEAVY')
   * @param {string} nationalStandard — brigade national standard
   * @param {number} targetStrength  — already-correct national-standard brigade strength
   * @param {string} brigadeId       — preserved so sub-unit parent refs stay valid
   * @param {string} brigadeName     — used in sub-unit name patterns
   * @returns {Array|null}
   */
  static generateScaledSubUnitsForTypeChange(
    unitType, nationalStandard, targetStrength, brigadeId, brigadeName
  ) {
    if (
      this.isMedicalBrigade(unitType) ||
      unitType === 'ARTILLERY' ||
      CSS_BRIGADE_TEMPLATES.hasOwnProperty(unitType)
    ) {
      return null;
    }

    const subUnits = this.generateSubUnitsFromDoctrine(
      unitType, brigadeName, brigadeId, 'brigade'
    );
    const rawSum = subUnits.reduce((s, su) => s + su.strength, 0);

    const natStrength = (nationalStandard && nationalStandard !== 'MULTINATIONAL')
      ? getDefaultStrength(nationalStandard, 'BRIGADE')
      : 0;
    const target = targetStrength || (natStrength > 0 ? natStrength : 0);

    if (target > 0 && target !== rawSum && rawSum > 0) {
      const scale = target / rawSum;
      let dist = 0;
      return subUnits.map((su, idx) => {
        if (idx === subUnits.length - 1) {
          return { ...su, strength: target - dist };
        }
        const s = Math.round(su.strength * scale);
        dist += s;
        return { ...su, strength: s };
      });
    }

    return subUnits;
  }

  // ============================================================================
  // BRIGADE BUILDER (ENHANCED v2.5.0 - Evacuation Chain Configuration)
  // ============================================================================

  /**
   * Build a complete brigade with MIXED battalions and Role 2 Basic facility.
   * v2.5.0 ENHANCEMENT: Configures evacuation chain (Role 2 pulls from Role 1s).
   */

  static buildBrigade(config) {
    
    // v3.0 PHASE 2: STRICT VALIDATION - NO FALLBACKS
    // Validate required fields before processing
    if (!config.unit_type) {
      throw new Error(
        `[v3.0 VALIDATION] buildBrigade requires 'unit_type' parameter.\n` +
        `Brigade: "${config.name || 'Unnamed'}"\n` +
        `Please select a Unit Type (MECHANIZED, HEAVY, INFANTRY, etc.) before saving.\n` +
        `This is required for doctrinal sub-unit generation in v3.0.`
      );
    }
    
    // Medical Brigade Special Handling
    if (this.isMedicalBrigade(config.unit_type)) {
      return this.buildMedicalBrigade(config);
    }
    
    const brigadeId = config.id || `brig_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    
    // v3.0.4 FIX (Feb 5, 2026): No fallback - nationalStandard must be provided
    const nationalStandard = config.nationalStandard;
    if (!nationalStandard) {
      throw new Error(`buildBrigade: Missing nationalStandard for brigade "${config.name}"`);
    }
    
    const unitType = normalizeUnitTypeKey(config.unit_type);  // v3.0: NO FALLBACK - already validated above; 2026-10-24: an old spelling (ENGINEER) is translated
    
    
    // Phase 2.1 Step 7: Check for manual HQ override from Tab2
    const override = config.scenario?.operation_parameters?.hq_override_config;
    const hasOverride = override?.enabled && override.brigade_hq_km !== null && override.brigade_hq_km !== undefined;
    
    // Get Battalion Composition
    const requestedBattalions = config.battalions || config.battalions_per_brigade;
    let expandedBattalions;
    let composition;
    let brigadePositionFeba;  // v2.11.0: CSS brigades positioned further back
    
    // v2.11.0 UT-001: Check for CSS brigade types first
    if (isCSS_BrigadeType(unitType)) {
      const cssTemplate = CSS_BRIGADE_TEMPLATES[unitType];
      expandedBattalions = this.getCSSBattalionComposition(unitType, requestedBattalions);
      composition = {
        template: cssTemplate.template,
        total_strength: cssTemplate.total_strength
      };
      // v2.4 Phase 2.1: Use base + offset from brigadeTypeOffsets.js (or override)
      if (hasOverride) {
        brigadePositionFeba = override.brigade_hq_km;
      } else {
        brigadePositionFeba = getBrigadeHQPosition(nationalStandard, unitType);
      }
    } else if (unitType === 'ARTILLERY') {
      expandedBattalions = this.getArtilleryBattalionComposition(requestedBattalions);
      composition = {
        template: ARTILLERY_BRIGADE_TEMPLATE.template,
        total_strength: ARTILLERY_BRIGADE_TEMPLATE.total_strength
      };
      // v2.4 Phase 2.1: Use base + offset from brigadeTypeOffsets.js (or override)
      if (hasOverride) {
        brigadePositionFeba = override.brigade_hq_km;
      } else {
        brigadePositionFeba = getBrigadeHQPosition(nationalStandard, unitType);
      }
    } else {
      composition = getBattalionComposition(unitType);
      expandedBattalions = expandBattalionComposition(unitType);
      // v2.4 Phase 2.1: Use base + offset from brigadeTypeOffsets.js (or override)
      if (hasOverride) {
        brigadePositionFeba = override.brigade_hq_km;
      } else {
        brigadePositionFeba = getBrigadeHQPosition(nationalStandard, unitType);
      }
    }
    
    // 2026-10-24 (R1-03 / R1-05): the cards of the brigade type, with the headquarters, the support battalion and the reconnaissance
    // unit as cards (constants/brigadeCards.js). The brigade total is their sum.
    const _brigadeCards = BRIGADE_CARDS_ON ? brigadeCardsFor(unitType) : null;   // 2026-10-25: the support brigade types that have a card list use it too (logistics)
    if (_brigadeCards) {
      expandedBattalions = _brigadeCards.map((c, i) => ({ ...c, battalion_number: i + 1 }));
    }

    // Adjust battalion count if specified (not for ARTILLERY or CSS)
    const isSpecialType = unitType === 'ARTILLERY' || isCSS_BrigadeType(unitType);
    if (requestedBattalions && requestedBattalions !== expandedBattalions.length && !isSpecialType && !_brigadeCards) {
      
      if (requestedBattalions > expandedBattalions.length) {
        const primaryType = unitType === 'ARMOR' ? 'ARMOR' : 'MECHANIZED';
        while (expandedBattalions.length < requestedBattalions) {
          expandedBattalions.push({
            battalion_number: expandedBattalions.length + 1,
            unit_type: primaryType,
            strength: BATTALION_TYPES[primaryType]?.typical_strength || 800,
            is_frontline: true,
            mission: 'MAIN_DEFENSE'
          });
        }
      } else if (requestedBattalions < expandedBattalions.length) {
        expandedBattalions = expandedBattalions.slice(0, requestedBattalions);
      }
    }
    
    
    // v3.0.6: Calculate breakdown from nationalStandards.js v2.6 breakdown_ratios
    const standard = NATIONAL_STANDARDS[nationalStandard];
    
    const hqPersonnel = standard?.brigade?.hq_personnel || 200;  // Brigade HQ only
    const breakdownRatios = standard?.brigade?.breakdown_ratios;
    const breakdown = breakdownRatios ? {
      command: Math.round(hqPersonnel * breakdownRatios.command),
      support: Math.round(hqPersonnel * breakdownRatios.support),
      combat: 0  // Combat comes from battalions, not HQ
    } : null;

    // BUG-113 FIX (March 16, 2026): National standard brigade strength was never applied
    // for maneuver brigades — composition.total_strength (from unitTypes.js, type-based)
    // always won over nationalStandards.js brigade.strength (nation-based).
    // Result: Romanian (4,000) and Polish (4,500) brigades both displayed NATO/MULTINATIONAL
    // strength (5,000). Fix: prefer national standard strength when the standard is a real
    // nation (not MULTINATIONAL which is an averaged placeholder, not a real brigade strength).
    // Priority: config.strength (manual override) > national standard > composition > battalions.
    const _natBrigadeStrength = (nationalStandard !== 'MULTINATIONAL' && standard?.brigade?.strength)
      ? standard.brigade.strength
      : null;
    const totalStrength = (_brigadeCards ? brigadeCardsTotal(_brigadeCards) : null) || config.strength || _natBrigadeStrength || composition.total_strength ||
      expandedBattalions.reduce((sum, bn) => sum + bn.strength, 0);
    
    const brigade = new Brigade({
      id: brigadeId,
      name: config.name,
      brigade_number: config.brigade_number || 1,
      nationalStandard: nationalStandard,
      strength: totalStrength,
      breakdown: breakdown,  // v3.0.6: Command/support breakdown
      // v2.11.0: CSS brigades positioned further from FEBA
      position_feba_km: config.position_feba_km !== undefined ? config.position_feba_km : brigadePositionFeba,
      parent_division_id: config.parent_division_id
    });
    
    brigade.unit_type = unitType;
    brigade.brigade_type = unitType;   // 2026-10-19 (R1-01): the label followed the Brigade default 'INFANTRY' for every brigade
    brigade.is_medical_provider = false;
    brigade.has_organic_role1 = true;
    brigade.has_organic_role2_basic = true;
    
    
    // v3.0 PHASE 2: Generate sub_units from doctrinal composition
    // NO FALLBACKS: Throws errors if composition missing
    try {
      brigade.sub_units = this.generateSubUnitsFromDoctrine(
        unitType,
        config.name,
        brigade.id,
        'brigade'
      );
      brigade.composition_source = 'doctrinal';
      
      
      // Recalculate total strength from sub_units for accuracy
      const calculatedStrength = brigade.sub_units.reduce((sum, subUnit) => sum + subUnit.strength, 0);
      brigade.strength = calculatedStrength;

      // BUG-113 FIX (March 16, 2026) — FINAL: Apply national standard brigade strength
      // after sub_units recalculation overwrites brigade.strength with composition total.
      // Uses getDefaultStrength() (proven function) rather than direct property access.
      // MULTINATIONAL excluded — averaged placeholder. CSS excluded — their
      // sub_units sum IS the correct strength.
      // Sub_units scaled proportionally so validateBrigadeData (±1 tolerance) still passes.
      // BUG-199 FIX (March 25, 2026): Removed `unitType !== 'ARTILLERY'` exclusion.
      // Original BUG-113 excluded ARTILLERY on the assumption that its doctrinal sub_units
      // sum is the authoritative strength. That holds for a standalone artillery brigade
      // built with nationalStandard='NATO'/'MULTINATIONAL'. However, buildDivision slot 3
      // defaults to ARTILLERY type — when a multinational division assigns a named standard
      // (e.g. GERMAN 4,800) to slot 3, the ARTILLERY exclusion suppressed the national
      // standard application and left brigade.strength at the raw doctrinal sub_units sum
      // (~2,600), causing ~275 casualty undercount for Bde 3 across all MND scenarios.
      // Fix: CSS check alone correctly excludes LOGISTICS/ENGINEER/SIGNAL/SUPPORT/MEDICAL.
      // ARTILLERY is a combat-support type (not CSS) and must receive national standard
      // strength when a named standard is present, exactly like MECHANIZED/HEAVY/INFANTRY.
      if (!isCSS_BrigadeType(unitType)) {
        const _natStrength = nationalStandard !== 'MULTINATIONAL'
          ? getDefaultStrength(nationalStandard, 'BRIGADE')
          : 0;
        // 2026-10-24: with the brigade cards the target is the sum of the cards (the doctrinal sub_units are scaled to it, as for a national standard)
        const _targetStr = _brigadeCards ? brigadeCardsTotal(_brigadeCards) : (config.strength || (_natStrength > 0 ? _natStrength : 0));
        if (_targetStr > 0 && _targetStr !== calculatedStrength && calculatedStrength > 0) {
          const _scale = _targetStr / calculatedStrength;
          let _dist = 0;
          brigade.sub_units = brigade.sub_units.map((su, idx) => {
            if (idx === brigade.sub_units.length - 1) {
              return { ...su, strength: _targetStr - _dist };
            }
            const _s = Math.round(su.strength * _scale);
            _dist += _s;
            return { ...su, strength: _s };
          });
          brigade.strength = _targetStr;
        }
      }
      
    } catch (error) {
      // If doctrinal composition fails, log error and throw
      throw new Error(`buildBrigade: Failed to generate sub_units for ${unitType} brigade '${config.name}': ${error.message}`);
    }
    
    // Create Mixed Battalions
    const battalions = [];
    const role1Facilities = [];
    const role1FacilityIds = [];  // v2.5.0: Track for evacuation chain
    
    const battalionSpecs = config.custom_battalions || expandedBattalions;
    
    for (let bnIndex = 0; bnIndex < battalionSpecs.length; bnIndex++) {
      const bnSpec = battalionSpecs[bnIndex];
      const bnNumber = bnSpec.battalion_number || (bnIndex + 1);
      const bnType = bnSpec.unit_type;
      const bnTypeConfig = BATTALION_TYPES[bnType] || BATTALION_TYPES.MECHANIZED;
      
      const bnName = bnSpec.label ? `${config.name} ${bnSpec.label}` : this._generateBattalionName(config.name, bnNumber, bnType);
      
      // v2.11.0: Position CSS battalions with their brigade (rear area)
      let bnPosition = bnTypeConfig.typical_position_km;
      if (bnSpec.position === 'BRIGADE') bnPosition = brigade.position_feba_km;   // 2026-10-25: the position the brigade really has (a position passed in the config wins over the type default)
      else if (bnSpec.position === 'SUPPORT_AREA') bnPosition = Math.max(BRIGADE_SUPPORT_AREA_KM, brigade.position_feba_km);
      if (bnPosition === undefined) {
        if (isCSS_BrigadeType(unitType)) {
          // CSS battalions stay with their brigade
          bnPosition = brigadePositionFeba;
        } else if (bnType === 'ARTILLERY') {
          bnPosition = 18;
        } else {
          bnPosition = 8;
        }
      }
      
      const battalionConfig = {
        name: bnName,
        battalion_number: bnNumber,
        unit_type: bnType,
        nationalStandard: nationalStandard,
        strength: bnSpec.strength,
        position_feba_km: bnPosition,
        mission: bnSpec.mission || 'SUPPORTING',
        is_frontline: bnSpec.is_frontline !== undefined ? bnSpec.is_frontline : bnTypeConfig.has_frontage,
        parent_brigade_id: brigade.id,
        parent_brigade_name: config.name,  // v2.5.0: For hierarchy display
        parent_division_id: config.parent_division_id,
        parent_division_name: config.parent_division_name,
        parent_corps_name: config.parent_corps_name,
        position_anchor: (bnSpec.position === 'BRIGADE' || bnSpec.position === 'SUPPORT_AREA') ? bnSpec.position : null,   // 2026-10-25: follows the brigade position
        skip_role1: !this.battalionNeedsRole1(bnType) || bnSpec.role1 === false   // 2026-10-24: brigade HQ, support battalion and signal cards carry no Role 1
      };
      
      const battalionResult = this.buildBattalion(battalionConfig);
      
      if (brigade.addBattalion) {
        brigade.addBattalion(battalionResult.battalion);
      }
      battalions.push(battalionResult.battalion);
      
      if (battalionResult.role1) {
        role1Facilities.push(battalionResult.role1);
        role1FacilityIds.push(battalionResult.role1.facility_id);  // v2.5.0
      }
    }
    
    
    // 2026-10-25: a brigade with cards carries its cards as sub_units too (one list, not two with different names)
    if (_brigadeCards) {
      brigade.sub_units = this.subUnitsFromBattalions(brigade.id, battalions);
      brigade.composition_source = 'cards';
    }

    // 2026-10-25 (Dr. Tatar): the strength a fresh brigade holds is calculated from its units; current_strength was left at the constructor value (5,000
    // for engineer and logistics brigades) when the strength was recalculated from the composition. A new brigade is at full strength.
    brigade.current_strength = brigade.strength;

    // v2.5.0: Get brigade asset profile
    const brigadeAssetProfile = this.getBrigadeAssetProfile(unitType);
    
    // v2.7.0 EA-001: Get organic assets for this brigade type
    const role2OrganicAssets = getRole2BasicOrganicAssets(brigadeAssetProfile, nationalStandard);
    const role2AssetStructure = createFacilityAssetStructure(role2OrganicAssets);
    
    // Create Role 2 Basic facility with evacuation chain
    // v2.8.0 MTF-001: Position = Brigade HQ + Role 2 Basic offset
    const role2Position = calculateMTFPosition(brigade.position_feba_km, 'ROLE2_BASIC');
    const role2 = new Role2BasicFacility({
      facility_id: brigade.generateFacilityId ? brigade.generateFacilityId() : `role2b_${brigadeId}`,
      facility_name: brigade.generateFacilityName ? brigade.generateFacilityName() : `${config.name} Medical Company`,
      parent_unit_id: brigade.id,
      parent_brigade_id: brigade.id,
      position_feba_km: role2Position,
      // v2.9.0 MTF-002: Beds and surgical teams from doctrine constants
      beds: ROLE_2_BASIC_DEFINITION.holding_capacity.beds,                     // 30 (was 50)
      surgical_teams: ROLE_2_BASIC_DEFINITION.surgical_capability.teams,       // 2 (same)
      // v2.5.0: Brigade type for asset allocation
      brigade_type: brigadeAssetProfile,
      // v2.5.0: Configure supported Role 1s for PULL
      supported_role1s: role1FacilityIds,
      // v2.7.0 EA-001/EA-002: Organic asset allocation with tracking structure
      organic_assets: role2AssetStructure.organic_assets,
      attached_assets: role2AssetStructure.attached_assets,
      detached_assets: role2AssetStructure.detached_assets,
      effective_assets: calculateEffectiveAssets(role2AssetStructure),
      // v2.5.0: Hierarchy
      hierarchy: {
        corps: config.parent_corps_name || null,
        division: config.parent_division_name || null,
        brigade: config.name,
        battalion: null
      }
    });
    
    // Link facility to brigade
    if (brigade.linkRole2BasicFacility) {
      brigade.linkRole2BasicFacility(role2);
    }
    
    // Set default personnel
    if (role2.setPersonnel) {
      role2.setPersonnel({
        medical_officers: 8,
        nurses: 20,
        medics: 30,
        support_staff: 12
      });
    }
    
    // Set default blood products
    if (role2.resupplyBlood) {
      role2.resupplyBlood({
        prbc_units: 100,
        ffp_units: 100,
        platelet_units: 50
      });
    }
    
    // v2.5.0: Configure evacuation chain - Role 1s deliver to this Role 2
    role1Facilities.forEach(role1 => {
      if (role1.setDeliveryDestination) {
        role1.setDeliveryDestination(role2.facility_id);
      }
    });
    
    
    const compositionSummary = this._summarizeBattalionComposition(battalions);
    
    // v3.0 PHASE 2: Validate brigade data completeness before return
    // NO FALLBACKS: Throws errors if data incomplete
    try {
      this.validateBrigadeData(brigade);
    } catch (error) {
      throw new Error(`buildBrigade: Validation failed for brigade '${config.name}': ${error.message}`);
    }
    
    return {
      brigade: brigade,
      battalions: battalions,
      role2: role2,
      role1_facilities: role1Facilities,
      is_medical_brigade: false,
      composition_summary: compositionSummary,
      // v2.5.0: Evacuation chain info
      evacuation_chain: {
        role1_ids: role1FacilityIds,
        role2_id: role2.facility_id,
        method: 'PULL'
      },
      summary: brigade.getSummary ? brigade.getSummary() : { id: brigade.id, name: brigade.name }
    };
  }
  
  /**
   * The sub_units of a brigade as a mirror of its cards (2026-10-25, Dr. Tatar: two lists described the same people with different names).
   * Before, a brigade with cards also carried the older doctrinal sub_units (US names: 'Combined Arms Battalion', 'Cavalry Squadron',
   * 'Brigade Support Battalion'); the calculator reads the cards when a brigade has them, but Tab 3 (MedicalService) named the Role 1 aid stations
   * after these sub_units by position, and Tab 3 / Tab 4 read their unit types. Now the sub_units ARE the cards (same id, name, type, strength).
   * @param {string} brigadeId
   * @param {Array} battalions the battalion objects (cards) of the brigade
   */
  static subUnitsFromBattalions(brigadeId, battalions) {
    return (battalions || []).map((bn) => {
      const row = {
        id: bn.id,
        type: 'BATTALION',
        unit_type: bn.unit_type,
        strength: bn.strength,
        name: bn.name,
        parent_id: brigadeId,
        notes: '',
        composition_source: 'cards'
      };
      if (typeof bn.position_feba_km === 'number') row.position_feba_km = bn.position_feba_km;
      return row;
    });
  }

  /**
   * The units of a brigade of a given type, built exactly as a fresh quick build builds them (the cards of the type, their strengths, positions
   * and Role 1 facilities; the brigade total is the sum of the cards). Used when the planner changes the type of an existing brigade or adds a
   * brigade to a division: before 2026-10-25 both paths used the older doctrinal template and typical battalion strengths, so the brigade kept the
   * old composition (Dr. Tatar: 'it holds the old cards'). The brigade keeps its id and parents, so its units point at it.
   * @param {Object} config id, name, unit_type, nationalStandard, parent_division_id, parent_division_name, parent_corps_name, scenario
   * @returns {{strength:number, sub_units:Array, composition_source:string, battalions:Array, role1_facilities:Array}}
   */
  static buildBrigadeUnitsForType(config) {
    if (!config || !config.id) throw new Error('buildBrigadeUnitsForType: config.id is required (the brigade keeps its id)');
    const built = this.buildBrigade({
      ...config,
      nationalStandard: config.nationalStandard === 'MULTINATIONAL' ? 'NATO' : config.nationalStandard
    });
    const b = built.brigade;
    return {
      strength: b.strength,
      sub_units: JSON.parse(JSON.stringify(b.sub_units || [])),
      composition_source: b.composition_source,
      battalions: built.battalions || [],
      role1_facilities: built.role1_facilities || []
    };
  }

  // ============================================================================
  // HELPER: Generate Battalion Name
  // ============================================================================
  
  static _generateBattalionName(brigadeName, battalionNumber, battalionType) {
    // v2.6.1: Fixed ENGINEER→ENGINEERS to match unitTypes.js
    // v2.11.0: Added CSS battalion type labels
    const typeLabels = {
      // Combat types
      INFANTRY: 'Infantry',
      MECHANIZED: 'Mechanized',
      ARMOR: 'Armor',
      RECONNAISSANCE: 'Reconnaissance',
      SPECIAL_FORCES: 'Special Forces', ATTACK_AVIATION: 'Attack Aviation', MILITARY_POLICE: 'Military Police',
      CBRN_DEFENSE: 'CBRN Defence', CIVIL_AFFAIRS: 'Civil Affairs', PSYCHOLOGICAL_OPS: 'Psychological Operations',
      ARTILLERY: 'Artillery',
      AIR_DEFENSE: 'Air Defense',
      ENGINEERS: 'Engineer',
      
      // v2.11.0 UT-001: CSS battalion types
      SUPPLY: 'Supply',
      TRANSPORT: 'Transport',
      MAINTENANCE: 'Maintenance',
      LOGISTICS: 'Logistics',
      SIGNAL: 'Signal',
      
      // Support types
      MEDICAL: 'Medical',
      HQ: 'HQ'
    };
    
    const typeLabel = typeLabels[battalionType] || battalionType;
    
    if (battalionType === 'HQ') {
      return `${brigadeName} HQ`;
    }
    
    return `${battalionNumber}/${brigadeName} ${typeLabel}`;
  }
  
  // ============================================================================
  // HELPER: Summarize Battalion Composition
  // ============================================================================
  
  static _summarizeBattalionComposition(battalions) {
    const summary = {
      total_battalions: 0,
      combat_battalions: 0,
      support_battalions: 0,
      total_strength: 0,
      weighted_casualty_modifier: 0,
      by_type: {}
    };
    
    let totalWeightedModifier = 0;
    
    for (const bn of battalions) {
      summary.total_battalions++;
      
      const unitType = bn.unit_type || 'MECHANIZED';
      const strength = bn.strength || 800;
      summary.total_strength += strength;
      
      const unitTypeConfig = BATTALION_TYPES[unitType] || BATTALION_TYPES.MECHANIZED;
      const modifier = unitTypeConfig.casualty_rate_modifier || 1.0;
      totalWeightedModifier += modifier * strength;
      
      const needsRole1 = this.battalionNeedsRole1(unitType);
      
      if (!summary.by_type[unitType]) {
        summary.by_type[unitType] = {
          count: 0,
          total_strength: 0,
          has_role1: needsRole1
        };
      }
      summary.by_type[unitType].count++;
      summary.by_type[unitType].total_strength += strength;
      
      if (needsRole1) {
        summary.combat_battalions++;
      } else {
        summary.support_battalions++;
      }
    }
    
    if (summary.total_strength > 0) {
      summary.weighted_casualty_modifier = Math.round((totalWeightedModifier / summary.total_strength) * 1000) / 1000;
    }
    
    return summary;
  }
  
  // ============================================================================
  // DIVISION BUILDER (ENHANCED v2.5.0 - Evacuation Chain)
  // ============================================================================
  
  static buildDivision(config) {
    const divisionId = config.id || `div_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    
    // v3.0.4 FIX (Feb 5, 2026): No fallback - nationalStandard must be provided
    const nationalStandard = config.nationalStandard;
    if (!nationalStandard) {
      throw new Error(`buildDivision: Missing nationalStandard for division "${config.name}"`);
    }
    
    // v2.13.2: Use hq_personnel from nationalStandards.js v2.3 (not total force)
    const standard = NATIONAL_STANDARDS[nationalStandard];
    const hqPersonnel = standard?.division?.hq_personnel || 1000;  // Division HQ only
    
    // v3.0.6: Calculate breakdown from nationalStandards.js v2.6 breakdown_ratios
    
    const breakdownRatios = standard?.division?.breakdown_ratios;
    const breakdown = breakdownRatios ? {
      command: Math.round(hqPersonnel * breakdownRatios.command),
      support: Math.round(hqPersonnel * breakdownRatios.support)
    } : null;

    const defaultBrigades = getSubordinateCount(nationalStandard, 'DIVISION');
    
    const brigadeCount = config.brigades || config.brigades_per_division || defaultBrigades || 3;
    const battalionsPerBrigade = config.battalions_per_brigade;
    const brigadeStandards = config.brigadeStandards || {};
    
    if (battalionsPerBrigade) {
    }
    
    // Phase 2.1 Step 7: Check for manual HQ override from Tab2
    const override = config.scenario?.operation_parameters?.hq_override_config;
    let divisionHQPosition;
    if (override?.enabled && override.division_hq_km !== null && override.division_hq_km !== undefined) {
      divisionHQPosition = override.division_hq_km;
    } else {
      // v2.4 Phase 2.1: Get division HQ position from nationalStandards.js v2.4
      divisionHQPosition = standard?.division?.hq_position_feba_km || 25;  // Fallback to 25km
    }
    
    const division = new Division({
      id: divisionId,
      name: config.name,
      division_number: config.division_number || 1,
      nationalStandard: nationalStandard,
      strength: config.strength || hqPersonnel,  // v2.13.2: Use HQ personnel
      breakdown: breakdown,  // v3.0.6: Command/support breakdown
      position_feba_km: config.position_feba_km !== undefined ? config.position_feba_km : divisionHQPosition,
      parent_corps_id: config.parent_corps_id
    });
    
    const defaultBrigadeTypes = ['MECHANIZED', 'MECHANIZED', 'MECHANIZED'];
    const brigadeTypes = config.brigade_types || 
      Array(brigadeCount).fill(null).map((_, i) => defaultBrigadeTypes[i] || 'MECHANIZED');
    
    const brigades = [];
    const battalions = [];
    const role1Facilities = [];
    const role2Facilities = [];
    const role2FacilityIds = [];  // v2.5.0: Track for evacuation chain
    const role2EnhancedFacilities = [];
    const role3Facilities = [];
    const medicalBrigades = [];
    let hasMedicalBrigade = false;
    
    for (let i = 1; i <= brigadeCount; i++) {
      const brigadeType = brigadeTypes[i - 1] || 'MECHANIZED';
      const brigadeStandard = brigadeStandards[i] || nationalStandard;
      
      // v3.0.5 FIX: Use getBrigadeHQPosition(brigadeStandard, brigadeType) for type-correct HQ distance
      // OLD: Math.round((division.position_feba_km * 0.3) + (i * 5)) — geometric formula ignored unit type offset
      // NEW: national base + unit type offset (e.g. US Artillery = 20 + 5 = 25km, US Infantry = 20 + 0 = 20km)
      // This matches Tab1 handleUnitTypeChange fix (Feb 11, 2026) — same source, same result
      const brigadePosition = getBrigadeHQPosition(brigadeStandard, brigadeType);
      
      const brigadeConfig = {
        name: `${config.name} Brigade ${i}`,
        brigade_number: i,
        unit_type: brigadeType,
        nationalStandard: brigadeStandard,
        position_feba_km: brigadePosition,
        parent_division_id: division.id,
        parent_division_name: config.name,  // v2.5.0
        parent_corps_id: config.parent_corps_id,
        parent_corps_name: config.parent_corps_name,  // v2.5.0
        battalions_per_brigade: battalionsPerBrigade
      };
      
      const brigadeResult = this.buildBrigade(brigadeConfig);
      
      if (division.addBrigade) {
        division.addBrigade(brigadeResult.brigade);
      }
      
      if (brigadeResult.is_medical_brigade) {
        medicalBrigades.push(brigadeResult);
        hasMedicalBrigade = true;
        // 2026-10-25 (Dr. Tatar: its low rear-area losses are counted): the medical brigade is also in the brigade list, so Tab 2 configures it and
        // the calculation computes it; medical_brigades keeps the flag and the facilities it provides.
        brigades.push(brigadeResult.brigade);
        // 2026-10-25: the medical brigade cards are units of the force like any other cards (they appear in the card list under their brigade)
        if (brigadeResult.battalions) {
          battalions.push(...brigadeResult.battalions);
        }
        
        if (brigadeResult.role2_enhanced) {
          role2EnhancedFacilities.push(brigadeResult.role2_enhanced);
        }
        if (brigadeResult.role3) {
          role3Facilities.push(brigadeResult.role3);
        }
      } else {
        brigades.push(brigadeResult.brigade);
        if (brigadeResult.battalions) {
          battalions.push(...brigadeResult.battalions);
        }
        if (brigadeResult.role2) {
          role2Facilities.push(brigadeResult.role2);
          role2FacilityIds.push(brigadeResult.role2.facility_id);  // v2.5.0
        }
        role1Facilities.push(...brigadeResult.role1_facilities);
      }
    }
    
    // v2.6.0: CREATE ORGANIC DIVISION ROLE 2E (Field Hospital)
    // NATO doctrine: Each Division has an organic Role 2 Enhanced facility
    // This is separate from Medical Brigade R2E (which provides additional capacity)
    if (role2EnhancedFacilities.length === 0) {
      // No Medical Brigade in this Division - create organic Division R2E
      
      // v2.7.0 EA-001: Get organic assets for Division Role 2E
      const divRole2eOrganicAssets = getRole2EnhancedOrganicAssets(nationalStandard);
      const divRole2eAssetStructure = createFacilityAssetStructure(divRole2eOrganicAssets);
      
      // v2.8.0 MTF-001: Position = Division HQ + Role 2E offset
      const divRole2ePosition = calculateMTFPosition(division.position_feba_km, 'ROLE2_ENHANCED');
      
      const divisionRole2E = new Role2EnhancedFacility({
        facility_id: `role2e_div_${divisionId}`,
        facility_name: `${config.name} Field Hospital`,
        parent_unit_id: division.id,
        parent_division_id: division.id,
        position_feba_km: divRole2ePosition,
        // v2.9.0 MTF-002: Beds, surgical teams, ICU from doctrine constants
        beds: ROLE_2_ENHANCED_DEFINITION.holding_capacity.beds,                // 40 (was 100)
        surgical_teams: ROLE_2_ENHANCED_DEFINITION.surgical_capability.teams,  // 4 (same)
        icu_beds: ROLE_2_ENHANCED_DEFINITION.holding_capacity.icu_beds,        // 10 (was 20)
        is_from_medical_brigade: false,
        is_organic_division: true,  // Flag to identify organic Division R2E
        // v2.5.0: Division type for asset allocation
        division_type: 'STANDARD',
        // v2.7.0 EA-001/EA-002: Organic asset allocation with tracking structure
        organic_assets: divRole2eAssetStructure.organic_assets,
        attached_assets: divRole2eAssetStructure.attached_assets,
        detached_assets: divRole2eAssetStructure.detached_assets,
        effective_assets: calculateEffectiveAssets(divRole2eAssetStructure),
        // v2.5.0: Hierarchy
        hierarchy: {
          corps: config.parent_corps_name || null,
          division: config.name,
          brigade: null,
          battalion: null
        }
      });
      
      // Set personnel
      if (divisionRole2E.setPersonnel) {
        divisionRole2E.setPersonnel({
          medical_officers: 20,
          nurses: 50,
          medics: 40,
          support_staff: 30
        });
      }
      
      // Set blood products
      if (divisionRole2E.resupplyBlood) {
        divisionRole2E.resupplyBlood({
          prbc_units: 200,
          ffp_units: 200,
          platelet_units: 100
        });
      }
      
      role2EnhancedFacilities.push(divisionRole2E);
    }
    
    // v2.5.0: Configure Role 2E to pull from Role 2Bs
    role2EnhancedFacilities.forEach(role2e => {
      if (role2e.evacuation_config) {
        role2e.evacuation_config.picks_up_from = role2FacilityIds;
      }
      // Also set delivery destination on Role 2Bs to this Role 2E
      role2Facilities.forEach(role2 => {
        if (role2.setDeliveryDestination) {
          role2.setDeliveryDestination(role2e.facility_id);
        }
      });
    });
    
    if (role2EnhancedFacilities.length > 0) {
    }
    
    // BUG FIX (Jan 31, 2026): Don't overwrite Division HQ strength with brigade totals
    let totalStrength = 0;
    brigades.forEach(bde => totalStrength += bde.strength || 0);
    medicalBrigades.forEach(mb => { if (!brigades.some(b => b.id === mb.brigade.id)) totalStrength += mb.brigade.strength || 0; });   // 2026-10-25: counted once (it is in the brigade list)

    // v2.13.0: Division support troops (signal, medical, logistics, engineers, fires,
    // air defence) — personnel NOT inside any brigade, located in division rear area.
    // These hold no frontage and generate casualties at 0.10–0.15× combat rate (DNBI-dominant).
    const divisionSupportTroops = getSupportUnitsForEchelon(nationalStandard, 'division');
    totalStrength += divisionSupportTroops.total_organic_strength;

    // Store total force strength separately, don't overwrite Division HQ strength
    // division.strength = Division HQ personnel
    // totalStrength = brigades + division support troops

    if (hasMedicalBrigade) {
    }

    return {
      division: division,
      brigades: brigades,
      battalions: battalions,
      total_strength: totalStrength,
      support_troops: divisionSupportTroops,  // v2.13.0: division-level support units
      medical_brigades: medicalBrigades,
      has_medical_brigade: hasMedicalBrigade,
      role2_facilities: role2Facilities,
      role1_facilities: role1Facilities,
      role2_enhanced_facilities: role2EnhancedFacilities,
      role3_facilities: role3Facilities,
      // v2.5.0: Evacuation chain info
      evacuation_chain: {
        role1_count: role1Facilities.length,
        role2_ids: role2FacilityIds,
        role2e_ids: role2EnhancedFacilities.map(r => r.facility_id),
        role3_ids: role3Facilities.map(r => r.facility_id)
      },
      summary: division.getSummary ? division.getSummary() : { id: division.id, name: division.name }
    };
  }
  
  // ============================================================================
  // CORPS BUILDER (ENHANCED v2.5.0 - Full Evacuation Chain)
  // ============================================================================
  
  static buildCorps(config) {
    const corpsId = config.id || `corps_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    
    // v3.0.4 FIX (Feb 5, 2026): No fallback - nationalStandard must be provided
    const nationalStandard = config.nationalStandard;
    if (!nationalStandard) {
      throw new Error(`buildCorps: Missing nationalStandard for corps "${config.name}"`);
    }

    // v2.15.0 ENH-018 Phase F: Lead/Framework Nation for MULTINATIONAL corps support troops.
    // Only meaningful when nationalStandard === 'MULTINATIONAL'.
    // null/absent → use averaged MULTINATIONAL data (correct planning default).
    const corpsLeadNation = (nationalStandard === 'MULTINATIONAL' && config.corpsLeadNation)
      ? config.corpsLeadNation
      : null;
    
    // v2.13.2: Use hq_personnel from nationalStandards.js v2.3 (not total force)
    const standard = NATIONAL_STANDARDS[nationalStandard];
    const hqPersonnel = standard?.corps?.hq_personnel || 2000;  // Corps HQ only
    
    // v3.0.6: Calculate breakdown from nationalStandards.js v2.6 breakdown_ratios
    const breakdownRatios = standard?.corps?.breakdown_ratios;
    
    const breakdown = breakdownRatios ? {
      command: Math.round(hqPersonnel * breakdownRatios.command),
      support: Math.round(hqPersonnel * breakdownRatios.support)
    } : null;

    const defaultDivisions = getSubordinateCount(nationalStandard, 'CORPS');
    const divisionCount = config.divisions || defaultDivisions || 3;
    
    const brigadesPerDivision = config.brigades_per_division;
    const battalionsPerBrigade = config.battalions_per_brigade;
    
    if (brigadesPerDivision) {
    }
    if (battalionsPerBrigade) {
    }
    
    // v2.8.0 MTF-001: Log MTF positioning doctrine being used
    
    // v2.9.0 MTF-002: Log MTF capacity doctrine being used
    
    // Phase 2.1 Step 7: Check for manual HQ override from Tab2
    const override = config.scenario?.operation_parameters?.hq_override_config;
    let corpsHQPosition;
    if (override?.enabled && override.corps_hq_km !== null && override.corps_hq_km !== undefined) {
      corpsHQPosition = override.corps_hq_km;
    } else {
      // v2.4 Phase 2.1: Get corps HQ position from nationalStandards.js v2.4
      corpsHQPosition = standard?.corps?.hq_position_feba_km || 50;  // Fallback to 50km
    }
    
    const corps = new Corps({
      id: corpsId,
      name: config.name,
      corps_number: config.corps_number || 1,
      nationalStandard: nationalStandard,
      strength: config.strength || hqPersonnel,  // v2.13.2: Use HQ personnel
      breakdown: breakdown,  // v3.0.6: Command/support breakdown
      position_feba_km: config.position_feba_km !== undefined ? config.position_feba_km : corpsHQPosition,
      hq_position_km: config.hq_position_km || (corpsHQPosition - 10)  // HQ 10km forward of rear boundary
    });
    
    // v2.3 BUGFIX: Get division standards for multinational
    const divisionStandards = config.divisionStandards || config.division_standards || {};
    
    // Create divisions
    const divisions = [];
    const brigades = [];
    const battalions = [];
    const role1Facilities = [];
    const role2Facilities = [];
    const role2EnhancedFacilities = [];
    const role2eFacilityIds = [];  // v2.5.0
    const role3Facilities = [];
    const medicalBrigades = [];
    let hasMedicalBrigades = false;
    let divisionSupportStrength = 0;  // v2.13.0: accumulated division support troops
    const divisionSupportGroups = [];  // v2.14.0: per-division named support unit arrays
    
    for (let i = 1; i <= divisionCount; i++) {
      const divNationalStandard = divisionStandards[i] || nationalStandard;
      
      // v2.8.0 MTF-001: Calculate division position relative to corps
      // Divisions are FORWARD of corps HQ (closer to FEBA)
      // Spread divisions: each division at 40-60% of corps position
      const divisionPosition = Math.round(corps.position_feba_km * (0.4 + (i * 0.05)));
      
      const divisionConfig = {
        name: `${config.name} Division ${i}`,
        division_number: i,
        nationalStandard: divNationalStandard,
        brigades_per_division: brigadesPerDivision,
        battalions_per_brigade: battalionsPerBrigade,
        position_feba_km: divisionPosition,
        parent_corps_id: corps.id,
        parent_corps_name: config.name  // v2.5.0
      };
      
      const divisionResult = this.buildDivision(divisionConfig);
      
      if (corps.addDivision) {
        corps.addDivision(divisionResult.division);
      }
      divisions.push(divisionResult.division);
      
      if (divisionResult.brigades) brigades.push(...divisionResult.brigades);
      if (divisionResult.battalions) battalions.push(...divisionResult.battalions);
      
      role1Facilities.push(...divisionResult.role1_facilities);
      role2Facilities.push(...divisionResult.role2_facilities);
      
      if (divisionResult.role2_enhanced_facilities) {
        role2EnhancedFacilities.push(...divisionResult.role2_enhanced_facilities);
        role2eFacilityIds.push(...divisionResult.role2_enhanced_facilities.map(r => r.facility_id));
      }
      if (divisionResult.role3_facilities) {
        role3Facilities.push(...divisionResult.role3_facilities);
      }
      if (divisionResult.medical_brigades) {
        medicalBrigades.push(...divisionResult.medical_brigades);
        hasMedicalBrigades = true;
      }
      // v2.13.0: Accumulate division support troops strength for total
      divisionSupportStrength += divisionResult.support_troops?.total_organic_strength || 0;
      // v2.14.0: Capture per-division named unit array for Tab1 display
      divisionSupportGroups.push({
        division_id: divisionResult.division.id,
        division_name: divisionResult.division.name,
        national_standard: divNationalStandard,
        organic_units: divisionResult.support_troops?.organic_units || [],
        total_organic_strength: divisionResult.support_troops?.total_organic_strength || 0,
        by_category: divisionResult.support_troops?.by_category || {}
      });
    }
    
    // Create Corps Role 3 if no Medical Brigades provided one
    let corpsRole3 = null;
    if (role3Facilities.length === 0) {
      
      // v2.7.0 EA-001: Get organic assets for Corps Role 3
      const corpsRole3OrganicAssets = getRole3OrganicAssets(nationalStandard);
      const corpsRole3AssetStructure = createFacilityAssetStructure(corpsRole3OrganicAssets);
      
      // v2.8.0 MTF-001: Position = Corps HQ + Role 3 offset
      // Use hq_position_km (HQ location) + offset, not position_feba_km (rear boundary)
      const corpsRole3Position = calculateMTFPosition(corps.hq_position_km || corps.position_feba_km, 'ROLE3');
      
      corpsRole3 = new Role3Facility({
        facility_id: `role3_${corpsId}`,
        facility_name: `${config.name} Combat Support Hospital`,
        parent_unit_id: corps.id,
        parent_corps_id: corps.id,
        position_feba_km: corpsRole3Position,
        // v2.9.0 MTF-002: Beds, surgical teams, ICU from doctrine constants
        beds: ROLE_3_DEFINITION.holding_capacity.beds,                         // 150 (was 200)
        surgical_teams: ROLE_3_DEFINITION.surgical_capability.teams,           // 8 (was 10)
        icu_beds: ROLE_3_DEFINITION.holding_capacity.bed_breakdown.icu,        // 40 (same)
        national_standard: nationalStandard === 'ROMANIAN' ? 'ROMANIAN_CORPS' : 'STANDARD',
        // v2.5.0: Configure supported Role 2Es
        supported_role2es: role2eFacilityIds,
        // v2.7.0 EA-001/EA-002: Organic asset allocation with tracking structure
        organic_assets: corpsRole3AssetStructure.organic_assets,
        attached_assets: corpsRole3AssetStructure.attached_assets,
        detached_assets: corpsRole3AssetStructure.detached_assets,
        effective_assets: calculateEffectiveAssets(corpsRole3AssetStructure),
        hierarchy: {
          corps: config.name,
          division: null,
          brigade: null,
          battalion: null
        }
      });
      
      if (corpsRole3.setPersonnel) {
        corpsRole3.setPersonnel({
          medical_officers: 50,
          nurses: 120,
          medics: 60,
          support_staff: 50
        });
      }
      
      if (corpsRole3.resupplyBlood) {
        corpsRole3.resupplyBlood({
          prbc_units: 500,
          ffp_units: 500,
          platelet_units: 200,
          cryoprecipitate_units: 100
        });
      }
      
      role3Facilities.push(corpsRole3);
    }
    
    // v2.5.0: Configure Role 3 to pull from Role 2Es
    role3Facilities.forEach(role3 => {
      if (role3.evacuation_config) {
        role3.evacuation_config.picks_up_from = role2eFacilityIds;
      }
      // Set delivery destination on Role 2Es to Role 3
      role2EnhancedFacilities.forEach(role2e => {
        if (role2e.setDeliveryDestination) {
          role2e.setDeliveryDestination(role3.facility_id);
        }
      });
    });
    
    
    // BUG FIX (Feb 12, 2026): Calculate COMPLETE total strength (Corps + Divisions + Brigades)
    // PREVIOUS BUG: Only summed Division HQs, missed Corps HQ and all brigades!
    // USER IMPACT: Showed 24,000 personnel when actual force was 28,300 (15% undercount)
    // RESULT: All downstream calculations (casualties, medical, supply) were WRONG

    // v2.13.0: Corps-level support troops (NOT inside any division)
    // v2.15.0 ENH-018 Phase F: use lead/framework nation's corps entry when configured,
    //   otherwise fall back to MULTINATIONAL averaged data.
    const supportStandard = corpsLeadNation || nationalStandard;
    const corpsSupportTroops = getSupportUnitsForEchelon(supportStandard, 'corps');

    let totalStrength = corps.strength || 0;  // Corps HQ personnel

    // Add all Division HQ strengths
    divisions.forEach(div => totalStrength += div.strength || 0);

    // Add all Brigade strengths
    brigades.forEach(bde => totalStrength += bde.strength || 0);

    // Add medical brigade strengths if any
    medicalBrigades.forEach(mb => { if (!brigades.some(b => b.id === mb.brigade?.id)) totalStrength += (mb.brigade?.strength || 0); });   // 2026-10-25: counted once

    // v2.13.0: Add division support troops (accumulated from each buildDivision call)
    totalStrength += divisionSupportStrength;

    // v2.13.0: Add corps-level support troops
    totalStrength += corpsSupportTroops.total_organic_strength;

    // Keep corps.strength as Corps HQ personnel
    // totalStrength = HQ + divisions + brigades + div support troops + corps support troops
    
    // Create medical inventory summary
    // v3.1.0 FIX: Role 1 and Role 2 Basic bed counts now read from doctrine constants
    // (was hardcoded * 10 and * 50 — wrong values and APP_RULES 2.1 violation)
    // Role 1: ROLE_1_DEFINITION.holding_capacity.beds = 6 (stretcher treatment positions)
    // Role 2 Basic: ROLE_2_BASIC_DEFINITION.holding_capacity.beds = 30
    const medicalInventory = {
      role1: { count: role1Facilities.length, beds: role1Facilities.length * ROLE_1_DEFINITION.holding_capacity.beds },
      role2_basic: { count: role2Facilities.length, beds: role2Facilities.length * ROLE_2_BASIC_DEFINITION.holding_capacity.beds },
      role2_enhanced: { count: role2EnhancedFacilities.length, beds: role2EnhancedFacilities.reduce((sum, r) => sum + (r.beds || ROLE_2_ENHANCED_DEFINITION.holding_capacity.beds), 0) },
      role3: { count: role3Facilities.length, beds: role3Facilities.reduce((sum, r) => sum + (r.beds || ROLE_3_DEFINITION.holding_capacity.beds), 0) },
      total_beds: 0,
      total_facilities: 0
    };
    medicalInventory.total_beds = medicalInventory.role1.beds + medicalInventory.role2_basic.beds + 
                                  medicalInventory.role2_enhanced.beds + medicalInventory.role3.beds;
    medicalInventory.total_facilities = medicalInventory.role1.count + medicalInventory.role2_basic.count + 
                                        medicalInventory.role2_enhanced.count + medicalInventory.role3.count;
    
    
    // CRITICAL DEBUG (Feb 13, 2026): Confirm what's being returned
    
    return {
      corps: corps,
      divisions: divisions,
      brigades: brigades,
      battalions: battalions,
      total_strength: totalStrength,
      support_troops: corpsSupportTroops,           // v2.13.0: corps-level support units
      corps_lead_nation: corpsLeadNation,            // v2.15.0: null = MULTINATIONAL averaged; non-null = FNC nation
      division_support_strength: divisionSupportStrength,  // v2.13.0: sum of all division support troops
      division_support_groups: divisionSupportGroups,       // v2.14.0: per-division named unit arrays
      medical_brigades: medicalBrigades,
      has_medical_brigades: hasMedicalBrigades,
      role1_facilities: role1Facilities,
      role2_facilities: role2Facilities,
      role2_enhanced_facilities: role2EnhancedFacilities,
      role3_facilities: role3Facilities,
      role3_facility: role3Facilities[0] || null,
      medical_inventory: medicalInventory,
      // v2.5.0: Full evacuation chain
      evacuation_chain: {
        role1_count: role1Facilities.length,
        role2_count: role2Facilities.length,
        role2e_count: role2EnhancedFacilities.length,
        role3_count: role3Facilities.length,
        configured: true
      },
      summary: corps.getSummary ? corps.getSummary() : { id: corps.id, name: corps.name }
    };
  }
  
  // ============================================================================
  // v2.5.0: CONFIGURE EVACUATION CHAIN (POST-BUILD)
  // ============================================================================
  
  /**
   * Configure evacuation chain relationships after building
   * Use this to reconfigure chains or set up cross-division support
   * 
   * @param {Object} buildResult - Result from buildBrigade/Division/Corps
   * @param {Object} chainConfig - Custom chain configuration
   */
  static configureEvacuationChain(buildResult, chainConfig = {}) {
    const { 
      method = 'PULL',            // Default evacuation method
      helicopterPriority = null,   // Override helicopter priorities
      crossDivisionSupport = false // Allow cross-division evacuation
    } = chainConfig;
    
    
    // Store cross-division support flag at build result level for calculator access
    buildResult.cross_division_support = {
      enabled: crossDivisionSupport,
      // Phase 2: Will add per-division matrix here
      // allowed_pairs: [], // e.g., [{ from: 'div1', to: 'div2' }]
      // mascal_only: true
    };
    
    // Configure Role 1s
    if (buildResult.role1_facilities) {
      buildResult.role1_facilities.forEach(role1 => {
        if (role1.setEvacuationMethod) {
          role1.setEvacuationMethod('outgoing', method === 'PUSH' ? 'PUSH' : 'AUTO');
        }
        // Mark facility for cross-division support eligibility
        role1.cross_division_eligible = crossDivisionSupport;
      });
    }
    
    // Configure Role 2Bs
    if (buildResult.role2_facilities) {
      buildResult.role2_facilities.forEach(role2 => {
        if (role2.setEvacuationMethod) {
          role2.setEvacuationMethod('incoming', 'PULL');
          role2.setEvacuationMethod('outgoing', 'AUTO');
        }
        if (helicopterPriority && role2.evacuation_config) {
          role2.evacuation_config.helicopter_priority = helicopterPriority;
        }
        // Role 2B can receive cross-division overflow
        role2.cross_division_eligible = crossDivisionSupport;
        role2.can_receive_cross_division = crossDivisionSupport;
      });
    }
    
    // Configure Role 2Es
    if (buildResult.role2_enhanced_facilities) {
      buildResult.role2_enhanced_facilities.forEach(role2e => {
        if (role2e.setEvacuationMethod) {
          role2e.setEvacuationMethod('incoming', 'PULL');
          role2e.setEvacuationMethod('outgoing', 'AUTO');
        }
        // Role 2E is primary cross-division receiver (more capacity)
        role2e.cross_division_eligible = crossDivisionSupport;
        role2e.can_receive_cross_division = crossDivisionSupport;
      });
    }
    
    // Configure Role 3s
    if (buildResult.role3_facilities) {
      buildResult.role3_facilities.forEach(role3 => {
        if (role3.setEvacuationMethod) {
          role3.setEvacuationMethod('incoming', 'PULL');
        }
        if (helicopterPriority && role3.evacuation_config) {
          role3.evacuation_config.helicopter_priority = 'HIGH';
        }
        // Role 3 always receives cross-division (Corps level)
        role3.cross_division_eligible = true;
        role3.can_receive_cross_division = true;
      });
    }
    
    
    return buildResult;
  }
  
  // ============================================================================
  // BUILD FORCE SHORTCUT (Enhanced v2.5.0)
  // ============================================================================
  
  static buildForce(echelon, config) {
    let buildResult;
    
    switch (echelon.toUpperCase()) {
      case 'BATTALION':
        buildResult = this.buildBattalion(config);
        break;
      case 'BRIGADE':
        buildResult = this.buildBrigade(config);
        break;
      case 'DIVISION':
        buildResult = this.buildDivision(config);
        break;
      case 'CORPS':
        buildResult = this.buildCorps(config);
        break;
      default:
        throw new Error(`Unknown echelon: ${echelon}`);
    }
    
    // v2.5.0: Auto-configure evacuation chain
    if (config.configureEvacuationChain !== false) {
      this.configureEvacuationChain(buildResult, config.evacuationConfig || {});
    }
    
    return this.formatBuildResult(echelon, buildResult);
  }
  
  // ============================================================================
  // FORMAT BUILD RESULT (v2.4.0 + v2.5.0 Enhancements)
  // ============================================================================
  
  static formatBuildResult(echelon, buildResult) {
    switch (echelon.toUpperCase()) {
      case 'BATTALION': {
        // F2-ARCH-010: always expose arrays alongside singular for consumer consistency.
        // Singular field kept for Tab 1 UI backward compat.
        const bnFormatted = buildResult.battalion.toJSON ? buildResult.battalion.toJSON() : buildResult.battalion;
        return {
          echelon: 'BATTALION',
          battalion: bnFormatted,
          battalions: [bnFormatted],         // F2: array alias — consumers use this
          role1: buildResult.role1 ? (buildResult.role1.toJSON ? buildResult.role1.toJSON() : buildResult.role1) : null,
          has_role1: !!buildResult.role1,
          medical_facilities: buildResult.role1 ? [buildResult.role1.toJSON ? buildResult.role1.toJSON() : buildResult.role1] : [],
          summary: buildResult.summary,
          facility_count: {
            role1_facilities: buildResult.role1 ? 1 : 0,
            total_facilities: buildResult.role1 ? 1 : 0
          }
        };
      }
        
      case 'BRIGADE': {
        // F2-ARCH-010: always expose brigades[] array alongside singular brigade.
        // Singular field kept for Tab 1 UI backward compat.
        const bdeFormatted = buildResult.brigade.toJSON ? buildResult.brigade.toJSON() : buildResult.brigade;

        if (buildResult.is_medical_brigade) {
          return {
            echelon: 'BRIGADE',
            is_medical_brigade: true,
            brigade: bdeFormatted,
            brigades: [bdeFormatted],        // F2: array alias — consumers use this
            battalions: (buildResult.battalions || []).map(bn => bn.toJSON ? bn.toJSON() : bn),   // 2026-10-25: the medical brigade cards
            role1_facilities: [],
            provides: buildResult.provides,
            role2_enhanced: buildResult.role2_enhanced?.toJSON ? buildResult.role2_enhanced.toJSON() : buildResult.role2_enhanced,
            role3: buildResult.role3?.toJSON ? buildResult.role3.toJSON() : buildResult.role3,
            medical_facilities: [
              buildResult.role2_enhanced?.toJSON ? buildResult.role2_enhanced.toJSON() : buildResult.role2_enhanced,
              buildResult.role3?.toJSON ? buildResult.role3.toJSON() : buildResult.role3
            ].filter(Boolean),
            summary: buildResult.summary,
            facility_count: {
              role2_enhanced_facilities: buildResult.role2_enhanced ? 1 : 0,
              role3_facilities: buildResult.role3 ? 1 : 0,
              total_facilities: (buildResult.role2_enhanced ? 1 : 0) + (buildResult.role3 ? 1 : 0)
            }
          };
        }

        return {
          echelon: 'BRIGADE',
          brigade: bdeFormatted,
          brigades: [bdeFormatted],          // F2: array alias — consumers use this
          battalions: buildResult.battalions.map(bn => bn.toJSON ? bn.toJSON() : bn),
          role2: buildResult.role2.toJSON ? buildResult.role2.toJSON() : buildResult.role2,
          role1_facilities: buildResult.role1_facilities.map(r1 => r1.toJSON ? r1.toJSON() : r1),
          composition_summary: buildResult.composition_summary,
          evacuation_chain: buildResult.evacuation_chain,  // v2.5.0
          medical_facilities: [
            buildResult.role2.toJSON ? buildResult.role2.toJSON() : buildResult.role2,
            ...buildResult.role1_facilities.map(r1 => r1.toJSON ? r1.toJSON() : r1)
          ],
          summary: buildResult.summary,
          facility_count: {
            role1_facilities: buildResult.role1_facilities.length,
            role2_facilities: 1,
            total_facilities: buildResult.role1_facilities.length + 1
          }
        };
      }
        
      case 'DIVISION': {
        const allDivFacilities = [
          ...buildResult.role2_facilities.map(r2 => r2.toJSON ? r2.toJSON() : r2),
          ...(buildResult.role2_enhanced_facilities || []).map(r2e => r2e.toJSON ? r2e.toJSON() : r2e),
          ...(buildResult.role3_facilities || []).map(r3 => r3.toJSON ? r3.toJSON() : r3),
          ...buildResult.role1_facilities.map(r1 => r1.toJSON ? r1.toJSON() : r1)
        ];
        // F2-ARCH-010: always expose divisions[] array alongside singular division.
        // Singular field kept for Tab 1 UI backward compat.
        const divFormatted = buildResult.division.toJSON ? buildResult.division.toJSON() : buildResult.division;
        const divBrigades  = buildResult.brigades.map(brig => brig.toJSON ? brig.toJSON() : brig);

        return {
          echelon: 'DIVISION',
          division: divFormatted,
          divisions: [divFormatted],         // F2: array alias — consumers use this
          brigades: divBrigades,
          battalions: buildResult.battalions?.map(bn => bn.toJSON ? bn.toJSON() : bn) || [],
          medical_brigades: buildResult.medical_brigades?.map(mb => ({
            brigade: mb.brigade.toJSON ? mb.brigade.toJSON() : mb.brigade,
            provides: mb.provides,
            role2_enhanced: mb.role2_enhanced?.toJSON ? mb.role2_enhanced.toJSON() : mb.role2_enhanced,
            role3: mb.role3?.toJSON ? mb.role3.toJSON() : mb.role3
          })) || [],
          medical_facilities: allDivFacilities,
          role2_enhanced_facilities: (buildResult.role2_enhanced_facilities || []).map(r2e => r2e.toJSON ? r2e.toJSON() : r2e),
          role3_facilities: (buildResult.role3_facilities || []).map(r3 => r3.toJSON ? r3.toJSON() : r3),
          has_medical_brigade: buildResult.has_medical_brigade,
          evacuation_chain: buildResult.evacuation_chain,  // v2.5.0
          summary: buildResult.summary,
          facility_count: {
            role1_facilities: buildResult.role1_facilities.length,
            role2_facilities: buildResult.role2_facilities.length,
            role2_enhanced_facilities: (buildResult.role2_enhanced_facilities || []).length,
            role3_facilities: (buildResult.role3_facilities || []).length,
            medical_brigades: buildResult.medical_brigades?.length || 0,
            total_facilities: allDivFacilities.length
          }
        };
      }

      case 'CORPS': {
        const allCorpsFacilities = [
          ...buildResult.role2_facilities.map(r2 => r2.toJSON ? r2.toJSON() : r2),
          ...(buildResult.role2_enhanced_facilities || []).map(r2e => r2e.toJSON ? r2e.toJSON() : r2e),
          ...(buildResult.role3_facilities || []).map(r3 => r3.toJSON ? r3.toJSON() : r3),
          ...buildResult.role1_facilities.map(r1 => r1.toJSON ? r1.toJSON() : r1)
        ];
        
        return {
          echelon: 'CORPS',
          corps: buildResult.corps.toJSON ? buildResult.corps.toJSON() : buildResult.corps,
          divisions: buildResult.divisions.map(div => div.toJSON ? div.toJSON() : div),
          brigades: buildResult.brigades?.map(brig => brig.toJSON ? brig.toJSON() : brig) || [],
          battalions: buildResult.battalions?.map(bn => bn.toJSON ? bn.toJSON() : bn) || [],
          medical_brigades: buildResult.medical_brigades?.map(mb => ({
            brigade: mb.brigade.toJSON ? mb.brigade.toJSON() : mb.brigade,
            provides: mb.provides,
            role2_enhanced: mb.role2_enhanced?.toJSON ? mb.role2_enhanced.toJSON() : mb.role2_enhanced,
            role3: mb.role3?.toJSON ? mb.role3.toJSON() : mb.role3
          })) || [],
          medical_facilities: allCorpsFacilities,
          role1_facilities: buildResult.role1_facilities.map(r1 => r1.toJSON ? r1.toJSON() : r1),
          role2_facilities: buildResult.role2_facilities.map(r2 => r2.toJSON ? r2.toJSON() : r2),
          role2_enhanced_facilities: (buildResult.role2_enhanced_facilities || []).map(r2e => r2e.toJSON ? r2e.toJSON() : r2e),
          role3_facilities: (buildResult.role3_facilities || []).map(r3 => r3.toJSON ? r3.toJSON() : r3),
          role3_facility: buildResult.role3_facility ? (buildResult.role3_facility.toJSON ? buildResult.role3_facility.toJSON() : buildResult.role3_facility) : null,
          has_medical_brigades: buildResult.has_medical_brigades,
          medical_inventory: buildResult.medical_inventory,
          evacuation_chain: buildResult.evacuation_chain,  // v2.5.0
          summary: buildResult.summary,
          facility_count: {
            role1_facilities: buildResult.role1_facilities.length,
            role2_facilities: buildResult.role2_facilities.length,
            role2_enhanced_facilities: (buildResult.role2_enhanced_facilities || []).length,
            role3_facilities: (buildResult.role3_facilities || []).length,
            medical_brigades: buildResult.medical_brigades?.length || 0,
            total_facilities: allCorpsFacilities.length
          }
        };
      }

      default:
        return buildResult;
    }
  }
  
  // ============================================================================
  // VALIDATION METHODS (v2.4.0)
  // ============================================================================
  
  static validateForceStructure(buildResult, echelon) {
    const issues = [];
    const warnings = [];
    
    switch (echelon.toUpperCase()) {
      case 'BATTALION':
        if (!buildResult.battalion) issues.push('Missing battalion');
        break;
        
      case 'BRIGADE':
        if (!buildResult.brigade) issues.push('Missing brigade');
        
        if (buildResult.is_medical_brigade) {
          if (!buildResult.role2_enhanced) {
            issues.push('Medical Brigade missing Role 2 Enhanced facility');
          }
          if (!buildResult.role3) {
            issues.push('Medical Brigade missing Role 3 facility');
          }
        } else {
          if (!buildResult.role2) issues.push('Combat brigade missing Role 2 Basic');
          if (buildResult.composition_summary) {
            const expectedRole1 = buildResult.composition_summary.combat_battalions;
            if (buildResult.role1_facilities.length !== expectedRole1) {
              warnings.push(`Expected ${expectedRole1} Role 1 facilities, found ${buildResult.role1_facilities.length}`);
            }
          }
        }
        break;
        
      case 'DIVISION':
        if (!buildResult.division) issues.push('Missing division');
        if (buildResult.has_medical_brigade) {
          if (!buildResult.role2_enhanced_facilities || buildResult.role2_enhanced_facilities.length === 0) {
            warnings.push('Medical Brigade present but no Role 2E facilities found');
          }
        }
        break;
        
      case 'CORPS':
        if (!buildResult.corps) issues.push('Missing corps');
        if (buildResult.has_medical_brigades) {
          if (!buildResult.role2_enhanced_facilities || buildResult.role2_enhanced_facilities.length === 0) {
            warnings.push('Medical Brigades present but no Role 2E facilities found');
          }
          if (!buildResult.role3_facilities || buildResult.role3_facilities.length === 0) {
            warnings.push('Medical Brigades present but no Role 3 facilities found');
          }
        }
        // v2.5.0: Validate evacuation chain
        if (!buildResult.evacuation_chain?.configured) {
          warnings.push('Evacuation chain not configured');
        }
        break;
        
      default:
        warnings.push(`Unknown echelon type: ${echelon}`);
        break;
    }
    
    return {
      valid: issues.length === 0,
      issues: issues,
      warnings: warnings,
      echelon: echelon,
      has_medical_brigades: buildResult.has_medical_brigade || buildResult.has_medical_brigades || false,
      facility_counts: {
        role1: buildResult.role1_facilities?.length || (buildResult.role1 ? 1 : 0),
        role2: buildResult.role2_facilities?.length || (buildResult.role2 ? 1 : 0),
        role2_enhanced: buildResult.role2_enhanced_facilities?.length || (buildResult.role2_enhanced ? 1 : 0),
        role3: buildResult.role3_facilities?.length || (buildResult.role3 ? 1 : 0)
      },
      evacuation_chain_configured: buildResult.evacuation_chain?.configured || false
    };
  }
}

// ============================================================================
// EXPORTS
// ============================================================================

export default ForceBuilder;
